import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { environment } from "./environment";
import { getToken } from "./App";
import { ArrowLeftIcon, PaperClipIcon, XMarkIcon, PlusIcon, PencilIcon, TrashIcon } from "@heroicons/react/24/outline";

const EMRTypeDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [emrType, setEmrType] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [documentationMethods, setDocumentationMethods] = useState([]);
  const [creatorUser, setCreatorUser] = useState(null);
  const [loadingCreator, setLoadingCreator] = useState(false);
  const [, setReport] = useState("No Response generated yet.");
  const [loadingReport, setLoadingReport] = useState(false);
  const [reportError, setReportError] = useState("");
  const [showErrorModal, setShowErrorModal] = useState(false);
  const fileInputRef = React.useRef();
  const [analyzeLoading, setAnalyzeLoading] = useState(false);
  const [analyzeError, setAnalyzeError] = useState("");
  const [analysisResults, setAnalysisResults] = useState(null);
  const [loadingResults, setLoadingResults] = useState(false);
  const [openPanels, setOpenPanels] = useState({});
  const [finalizeLoading, setFinalizeLoading] = useState(false);
  const [finalizeError, setFinalizeError] = useState("");
  const [showFinalizeErrorModal, setShowFinalizeErrorModal] = useState(false);
  const [showAnalyzeErrorModal, setShowAnalyzeErrorModal] = useState(false);

  // Add state for instruction modal
  const [showInstructionModal, setShowInstructionModal] = useState(false);
  const [editingInstruction, setEditingInstruction] = useState("");
  const [editingIndex, setEditingIndex] = useState(null);
  const [editingKey, setEditingKey] = useState("");
  const [editingValue, setEditingValue] = useState("");
  const [savingModalInstruction, setSavingModalInstruction] = useState(false);

  // Track if current user started the analysis
  const [userStartedAnalysis, setUserStartedAnalysis] = useState(false);

  // Add state for filtering analysis results
  const [filterText, setFilterText] = useState("");

  // Add state for status dropdown
  const [editingStatusIndex, setEditingStatusIndex] = useState(null);

  // Add state for status filter
  const [statusFilter, setStatusFilter] = useState("");
  const [statusDropdownOpen, setStatusDropdownOpen] = useState(false);

  // Add state for row selection
  const [selectedRows, setSelectedRows] = useState(new Set());
  const [selectAll, setSelectAll] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);

  // Add state for column sorting
  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: 'asc'
  });

  // Add state for grid expander
  const [isGridExpanded, setIsGridExpanded] = useState(false);

  // Add state for chunk-based response handling
  const [chunkResponse, setChunkResponse] = useState(null);
  const [selectedChunks, setSelectedChunks] = useState(new Set());
  const [selectedFields, setSelectedFields] = useState(new Set()); // New: individual field selection
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [confirmError, setConfirmError] = useState("");
  const [xpathGenerationErrors, setXpathGenerationErrors] = useState(null); // Store failed_fields from xpath_generation
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = React.useRef();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteItem, setDeleteItem] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [showDeleteSuccess, setShowDeleteSuccess] = useState(false);
  const [analyzeProgress, setAnalyzeProgress] = useState(null);
  const [progressInterval, setProgressInterval] = useState(null);
  const [bulkUpdateLoading, setBulkUpdateLoading] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [bulkUpdateStatus, setBulkUpdateStatus] = useState(null);

  // Add state for tabs
  const [activeTab, setActiveTab] = useState("analysis");

  // Add state for manual fields
  const [manualFields, setManualFields] = useState([]);
  const [loadingManualFields, setLoadingManualFields] = useState(false);
  const [showCreateManualFieldModal, setShowCreateManualFieldModal] = useState(false);
  const [creatingManualField, setCreatingManualField] = useState(false);
  const [showEditManualFieldModal, setShowEditManualFieldModal] = useState(false);
  const [editingManualField, setEditingManualField] = useState(null);
  const [editingManualFieldName, setEditingManualFieldName] = useState("");
  const [updatingManualField, setUpdatingManualField] = useState(false);
  const [deletingManualField, setDeletingManualField] = useState(false);
  const [manualFieldError, setManualFieldError] = useState("");
  const [showDeleteManualFieldModal, setShowDeleteManualFieldModal] = useState(false);
  const [fieldToDelete, setFieldToDelete] = useState(null);
  const [originalManualFieldId, setOriginalManualFieldId] = useState(null);

  // Add state for EMR Type Fields lookup
  const [emrTypeFields, setEmrTypeFields] = useState([]);
  const [loadingEmrTypeFields, setLoadingEmrTypeFields] = useState(false);
  const [emrTypeFieldsError, setEmrTypeFieldsError] = useState("");
  const [selectedLookupField, setSelectedLookupField] = useState(null);

  // Static options for Create Manual Field dropdown (shown first, then API fields)
  const createManualFieldOptions = useMemo(() => [
    { id: 'modalities_modality_steps', name: 'Modalities/Modality Steps', type: 'lookup', analyzable: 'not_for_analyzing' },
    { id: 'activities_sub_activities', name: 'Activities/Sub-Activities', type: 'lookup', analyzable: 'not_for_analyzing' },
    ...emrTypeFields
  ], [emrTypeFields]);

  // Add state for session instructions
  const [showSessionInstructionsModal, setShowSessionInstructionsModal] = useState(false);
  const [sessionInstructions, setSessionInstructions] = useState("");
  const [methodsInstructions, setMethodsInstructions] = useState("");
  const [progressTowardsGoalInstructions, setProgressTowardsGoalInstructions] = useState("");
  const [recommendedChangesInstructions, setRecommendedChangesInstructions] = useState("");
  const [topHeaderText, setTopHeaderText] = useState("");
  const [section1Header, setSection1Header] = useState("Section 1: Field \"Methods\"");
  const [section2Header, setSection2Header] = useState("Section 2: Field \"Progress_towards_goal\"");
  const [section3Header, setSection3Header] = useState("Section 3: Field \"Recommended_changes\"");
  const [savingSessionInstructions, setSavingSessionInstructions] = useState(false);
  const [sessionInstructionsError, setSessionInstructionsError] = useState("");
  const [sessionInstructionsSuccess, setSessionInstructionsSuccess] = useState("");
  const [showSessionInstructionsSuccess, setShowSessionInstructionsSuccess] = useState(false);

  useEffect(() => {
    const fetchEmrType = async () => {
      setLoading(true);
      setError("");
      try {
        const response = await fetch(`${environment.apiUrl}/emr-types/${id}`, {
          headers: {
            Authorization: `Bearer ${getToken()}`,
          },
        });
        if (response.ok) {
          const data = await response.json();
          setEmrType(data);

          // Fetch creator user if user_id exists
          if (data.user_id) {
            setLoadingCreator(true);
            try {
              const userResponse = await fetch(`${environment.apiUrl}/users/${data.user_id}`, {
                headers: {
                  Authorization: `Bearer ${getToken()}`,
                },
              });
              if (userResponse.ok) {
                const userData = await userResponse.json();
                setCreatorUser(userData);
              }
            } catch (err) {
              console.error("Error fetching creator user:", err);
            } finally {
              setLoadingCreator(false);
            }
          }

          // If EMR type is already processing, start progress polling automatically
          if (data.status === "processing") {
            console.log("EMR type is processing, starting automatic progress polling...");
            setUserStartedAnalysis(false); // Current user didn't start this analysis
            startProgressPolling();
          }

          // Check if we should auto-open the session instructions modal
          const urlParams = new URLSearchParams(location.search);
          if (urlParams.get('showInstructions') === 'true') {
            console.log('Auto-opening session instructions modal...');

            // Parse session instructions data for the modal
            if (data.methods_instructions) {
              const section1Pattern = 'Section 1: Field "Methods"';
              const lastSection1Index = data.methods_instructions.lastIndexOf(section1Pattern);

              if (lastSection1Index !== -1) {
                // Top content: everything until the last "Section 1: Field 'Methods'" (NOT including it)
                const topContent = data.methods_instructions.substring(0, lastSection1Index).trim();

                // Section 1 header: the last "Section 1: Field 'Methods'" occurrence
                const section1HeaderText = section1Pattern;

                // Section 1 content: everything after the last "Section 1: Field 'Methods'"
                const methodsContent = data.methods_instructions.substring(lastSection1Index + section1Pattern.length).trim();

                setTopHeaderText(topContent);
                setSection1Header(section1HeaderText);
                setMethodsInstructions(methodsContent);
              } else {
                // No Section 1 found, use everything as top content
                setTopHeaderText(data.methods_instructions);
                setMethodsInstructions("");
              }
            }

            // Parse progress_towards_goal_instructions field for Section 2
            if (data.progress_towards_goal_instructions) {
              const section2Pattern = 'Section 2: Field "Progress_towards_goal"';
              const section2Index = data.progress_towards_goal_instructions.indexOf(section2Pattern);

              if (section2Index !== -1) {
                // Section 2 header: "Section 2: Field 'Progress_towards_goal'"
                const section2HeaderText = section2Pattern;

                // Section 2 content: everything after the header
                const progressContent = data.progress_towards_goal_instructions.substring(section2Index + section2Pattern.length).trim();

                setSection2Header(section2HeaderText);
                setProgressTowardsGoalInstructions(progressContent);
              } else {
                // No header found, use everything as content
                setProgressTowardsGoalInstructions(data.progress_towards_goal_instructions);
              }
            }

            // Parse recommended_changes_instructions field for Section 3
            if (data.recommended_changes_instructions) {
              const section3Pattern = 'Section 3: Field "Recommended_changes"';
              const section3Index = data.recommended_changes_instructions.indexOf(section3Pattern);

              if (section3Index !== -1) {
                // Section 3 header: "Section 3: Field 'Recommended_changes'"
                const section3HeaderText = section3Pattern;

                // Section 3 content: everything after the header
                const recommendedContent = data.recommended_changes_instructions.substring(section3Index + section3Pattern.length).trim();

                setSection3Header(section3HeaderText);
                setRecommendedChangesInstructions(recommendedContent);
              } else {
                // No header found, use everything as content
                setRecommendedChangesInstructions(data.recommended_changes_instructions);
              }
            }

            // Small delay to ensure the component is fully loaded
            setTimeout(() => {
              setShowSessionInstructionsModal(true);
            }, 500);
          }
        } else {
          setError("Failed to fetch EMR type");
        }
      } catch (err) {
        setError("Network error");
      } finally {
        setLoading(false);
      }
    };

    const fetchDocumentationMethods = async () => {
      try {
        const response = await fetch(`${environment.apiUrl}/documentation-methods/`, {
          headers: {
            Authorization: `Bearer ${getToken()}`,
          },
        });

        if (response.ok) {
          const data = await response.json();
          setDocumentationMethods(data);
        }
      } catch (err) {
        console.error("Error fetching documentation methods:", err);
      }
    };

    fetchEmrType();
    fetchDocumentationMethods();
    // Clear any previous errors when component loads
    setReportError("");
  }, [id]);

  // Handle clicking outside dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    };

    if (dropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [dropdownOpen]);

  // Cleanup progress interval on unmount
  useEffect(() => {
    return () => {
      if (progressInterval) {
        clearInterval(progressInterval);
      }
    };
  }, [progressInterval]);

  // Close dropdown when instruction modal opens
  useEffect(() => {
    if (showInstructionModal) {
      setStatusDropdownOpen(false);
    }
  }, [showInstructionModal]);

  // Fetch analysis results on page load
  useEffect(() => {
    const loadAnalysisResults = async () => {
      try {
        const response = await fetch(`${environment.apiUrl}/emr-types/${id}/results`, {
          headers: {
            Authorization: `Bearer ${getToken()}`,
          },
        });

        if (response.ok) {
          const data = await response.json();
          if (data && data.length > 0) {
            setAnalysisResults(data);
          }
        }
        // Don't show error if no results exist yet
      } catch (err) {
        // Don't show error if no results exist yet
        console.log("No analysis results found yet");
      }
    };

    if (id) {
      loadAnalysisResults();
    }
  }, [id]);

  // Fetch response on page load
  useEffect(() => {
    const loadResponse = async () => {
      try {
        const response = await fetch(`${environment.apiUrl}/emr-types/${id}?response_only=true`, {
          headers: {
            Authorization: `Bearer ${getToken()}`,
          },
        });

        if (response.ok) {
          const data = await response.json();
          if (data.json_response) {
            setReport(data.json_response);
          }
        }
        // Don't show error if no response exists yet
      } catch (err) {
        // Don't show error if no response exists yet
        console.log("No response found yet");
      }
    };

    if (id) {
      loadResponse();
    }
  }, [id]);

  const handleAnalyze = async () => {
    console.log("=== handleAnalyze called ===");
    setAnalyzeLoading(true);
    setAnalyzeError("");
    setAnalysisResults(null);
    setChunkResponse(null);
    setSelectedChunks(new Set());
    setSelectedFields(new Set()); // Clear field selections too
    setConfirmError("");
    setAnalyzeProgress({ progress: 0, status: "starting", message: "Starting analysis..." });
    setUserStartedAnalysis(true); // Current user is starting the analysis

    try {
      // Start progress polling IMMEDIATELY when analysis starts
      console.log("Starting progress polling IMMEDIATELY...");
      startProgressPolling();

      // Update UI status to "processing" immediately
      setEmrType(prev => ({
        ...prev,
        status: "processing"
      }));

      // Step 1: POST to analyze endpoint
      const analyzeResponse = await fetch(`${environment.apiUrl}/ai/analyze-emr-type/${id}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getToken()}`,
        },
      });

      if (analyzeResponse.ok) {
        const analyzeData = await analyzeResponse.json();
        console.log("Analysis response:", analyzeData);
        console.log("chunks_available:", analyzeData.chunks_available);
        console.log("message:", analyzeData.message);

        // Check if response contains chunks (new format)
        if (analyzeData.chunks_available && analyzeData.all_chunks) {
          console.log("Got chunks data from POST response, setting dropdown");
          setChunkResponse(analyzeData);
          setDropdownOpen(false);
          setSelectedChunks(new Set());
          setSelectedFields(new Set()); // Clear field selections too
          setAnalysisResults(null);
          setAnalyzeProgress(null);
          // Stop progress polling since we got the data directly
          if (progressInterval) {
            clearInterval(progressInterval);
            setProgressInterval(null);
          }
        } else if (analyzeData.message === "Analysis completed successfully") {
          // Step 2: GET results after analysis is complete (old format)
          await fetchResults();
          setAnalyzeProgress(null);
        }

        // Step 3: Refresh EMR type data to get updated status
        const updatedResponse = await fetch(`${environment.apiUrl}/emr-types/${id}`, {
          headers: {
            Authorization: `Bearer ${getToken()}`,
          },
        });

        if (updatedResponse.ok) {
          const updatedData = await updatedResponse.json();
          setEmrType(updatedData);
        }
      } else {
        const data = await analyzeResponse.json().catch(() => ({}));
        const errorMessage = data.detail || "Failed to start analysis.";

        // Check if this is the specific "no more fields to analyze" error
        if (errorMessage.includes("There are no more fields to analyze") ||
          errorMessage.includes("All fields that needs to be analyzed is already analyzed")) {
          // Clean up the error message to remove the "Error analyzing EMR: 400:" prefix
          const cleanMessage = errorMessage.replace(/^Error analyzing EMR: \d+: /, '');
          setAnalyzeError(cleanMessage);
          setShowAnalyzeErrorModal(true);
        } else {
          setAnalyzeError(errorMessage);
        }

        setAnalyzeProgress(null);
        setUserStartedAnalysis(false); // Current user was not allowed to start
        // Stop progress polling since analysis failed to start
        if (progressInterval) {
          clearInterval(progressInterval);
          setProgressInterval(null);
        }
      }
    } catch (err) {
      setAnalyzeError("Network error during analysis");
      setAnalyzeProgress(null);
      // Stop progress polling since analysis failed
      if (progressInterval) {
        clearInterval(progressInterval);
        setProgressInterval(null);
      }
    } finally {
      setAnalyzeLoading(false);
    }
  };

  const startProgressPolling = () => {
    console.log("startProgressPolling called");

    // Clear any existing interval
    if (progressInterval) {
      clearInterval(progressInterval);
    }

    const interval = setInterval(async () => {
      console.log("Polling progress...");
      try {
        const response = await fetch(`${environment.apiUrl}/ai/analyze-progress/${id}`, {
          headers: {
            Authorization: `Bearer ${getToken()}`,
          },
        });

        if (response.ok) {
          const progressData = await response.json();
          console.log("Progress data received:", progressData);
          setAnalyzeProgress(progressData);

          // Update error message with current progress percentage if there's a real error
          if (analyzeError && analyzeError.includes("already in progress")) {
            if (progressData.status === "completed" || progressData.progress >= 100) {
              setAnalyzeError(""); // Clear error when analysis is complete
            } else {
              setAnalyzeError(`EMR analysis is already in progress (${progressData.progress}% complete). Please wait for it to complete.`);
            }
          }

          // If analysis is complete, stop polling
          if (progressData.status === "completed" || progressData.progress >= 100) {
            console.log("Analysis complete! userStartedAnalysis:", userStartedAnalysis);
            clearInterval(interval);
            setProgressInterval(null);
            setAnalyzeProgress(null);

            // Only show results if the current user started the analysis
            if (userStartedAnalysis) {
              console.log("Analysis completed via progress polling, fetching results...");
              // Try the regular results endpoint since chunks should already be handled by POST response
              await fetchResults();
            }

            // Refresh EMR type data
            const updatedResponse = await fetch(`${environment.apiUrl}/emr-types/${id}`, {
              headers: {
                Authorization: `Bearer ${getToken()}`,
              },
            });

            if (updatedResponse.ok) {
              const updatedData = await updatedResponse.json();
              setEmrType(updatedData);
            }
          }
        } else {
          console.error("Failed to fetch progress");
        }
      } catch (error) {
        console.error("Error fetching progress:", error);
      }
    }, 2000); // Poll every 2 seconds

    setProgressInterval(interval);
  };

  const fetchResults = async () => {
    setLoadingResults(true);
    try {
      const response = await fetch(`${environment.apiUrl}/emr-types/${id}/results`, {
        headers: {
          Authorization: `Bearer ${getToken()}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        console.log("Analysis results data:", data);
        // Ensure data is safe to render
        if (data && typeof data === 'object' && !Array.isArray(data)) {
          console.error("Analysis results is an object, not an array:", data);
          setAnalyzeError("Invalid analysis results format");
          setAnalyzeProgress(null);
          return;
        }
        setAnalysisResults(data);
      } else {
        const errorData = await response.json().catch(() => ({}));
        console.error("Failed to fetch analysis results:", errorData);
        setAnalyzeError("Failed to fetch analysis results");
        setAnalyzeProgress(null);
      }
    } catch (err) {
      console.error("Network error fetching results:", err);
      setAnalyzeError("Network error fetching results");
      setAnalyzeProgress(null);
    } finally {
      setLoadingResults(false);
    }
  };

  const handleGenerateResponse = async () => {
    setReportError("");
    setLoadingReport(true);

    // Immediately update UI status to "processing"
    setEmrType(prev => ({
      ...prev,
      status: "processing"
    }));

    try {
      // Step 1: POST to analyze-emr-file endpoint
      const response = await fetch(`${environment.apiUrl}/ai/generate-response-emr-type/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getToken()}`,
        },
        body: JSON.stringify({ emr_type_id: id }),
      });

      if (response.ok) {
        const data = await response.json();
        console.log("Analysis response:", data); // Should be {"message": "Analysis completed successfully"}

        // Step 2: Poll for status changes while processing
        let attempts = 0;
        const maxAttempts = 30; // 30 seconds max
        const pollInterval = setInterval(async () => {
          attempts++;

          try {
            const statusResponse = await fetch(`${environment.apiUrl}/emr-types/${id}`, {
              headers: {
                Authorization: `Bearer ${getToken()}`,
              },
            });

            if (statusResponse.ok) {
              const statusData = await statusResponse.json();
              setEmrType(statusData);

              // If status is no longer "processing", stop polling
              if (statusData.status !== "processing") {
                clearInterval(pollInterval);

                // If status is "generated", fetch the response
                if (statusData.status === "generated") {
                  const resultsResponse = await fetch(`${environment.apiUrl}/emr-types/${id}?response_only=true`, {
                    headers: {
                      Authorization: `Bearer ${getToken()}`,
                    },
                  });

                  if (resultsResponse.ok) {
                    const resultsData = await resultsResponse.json();
                    setReport(resultsData.json_response || "No response generated.");
                  } else {
                    const resultsError = await resultsResponse.json().catch(() => ({}));
                    setReportError(resultsError.detail || "Failed to fetch response.");
                  }
                }
              }
            }
          } catch (error) {
            console.error("Error polling status:", error);
          }

          // Stop polling after max attempts
          if (attempts >= maxAttempts) {
            clearInterval(pollInterval);
            setReportError("Generation timed out. Please try again.");
          }
        }, 1000); // Poll every 1 second

      } else {
        const data = await response.json().catch(() => ({}));
        console.log("Generate Response Error:", data); // Debug log
        const errorMessage = data.detail || data.error || "Failed to start analysis.";
        console.log("Setting error message:", errorMessage); // Debug log
        setReportError(errorMessage);
        setShowErrorModal(true);
      }
    } catch (err) {
      setReportError("Network error");
    } finally {
      setLoadingReport(false);
    }
  };

  const handleConfirmChunk = async () => {
    if (selectedChunks.size === 0 && selectedFields.size === 0) {
      setConfirmError("Please select at least one chunk or individual field first.");
      return;
    }

    // Clear any previous errors
    setConfirmError("");

    setConfirmLoading(true);

    try {
      // Process selected chunks (full chunks)
      const selectedChunkArray = Array.from(selectedChunks).map(chunkIndex => {
        const chunk = chunkResponse.all_chunks.find(c => c.chunk_index === chunkIndex);
        return {
          selected_chunk_index: chunk.chunk_index,
          selected_chunk_response: chunk.response,
          selected_chunk_label: chunk.label || null
        };
      });

      // Process selected individual fields
      const selectedFieldsArray = Array.from(selectedFields).map(fieldKey => {
        const [fieldName, chunkIndex] = fieldKey.split('|');
        const chunk = chunkResponse.all_chunks.find(c => c.chunk_index === parseInt(chunkIndex));

        // Extract the specific field value from the chunk response
        let fieldValue = 'Empty';
        if (chunk && typeof chunk.response === 'string') {
          const chunkData = {};
          chunk.response.split('\n').forEach(line => {
            const [key, ...valueParts] = line.split(':');
            if (key && valueParts.length > 0) {
              const value = valueParts.join(':').trim();
              const normalizedKey = key.trim().toLowerCase();
              chunkData[normalizedKey] = value;
            }
          });
          fieldValue = chunkData[fieldName] || 'Empty';
        }

        return {
          field_name: fieldName,
          field_value: fieldValue,
          chunk_index: parseInt(chunkIndex),
          chunk_label: chunk ? (chunk.label || null) : null
        };
      });

      const response = await fetch(`${environment.apiUrl}/ai/save-selected-chunk/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getToken()}`,
        },
        body: JSON.stringify({
          emr_type_id: id,
          selected_chunks: selectedChunkArray,
          selected_fields: selectedFieldsArray
        }),
      });

      if (response.ok) {
        const data = await response.json();
        console.log("Chunks saved successfully:", data);

        // Success case - BE only sends success messages now
        // Clear chunk response and selected chunks
        setChunkResponse(null);
        setSelectedChunks(new Set());
        setSelectedFields(new Set()); // Clear field selections too
        setDropdownOpen(false);

        // Fetch and display the analysis results after saving
        await fetchResults();

        // Refresh EMR type data to get updated status
        const updatedResponse = await fetch(`${environment.apiUrl}/emr-types/${id}`, {
          headers: {
            Authorization: `Bearer ${getToken()}`,
          },
        });

        if (updatedResponse.ok) {
          const updatedData = await updatedResponse.json();
          setEmrType(updatedData);
        }
      }
    } catch (err) {
      setConfirmError("Network error");
    } finally {
      setConfirmLoading(false);
    }
  };

  const handleDeleteClick = (item) => {
    setDeleteItem(item);
    setShowDeleteConfirm(true);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteItem) return;

    setDeleteLoading(true);

    try {
      const response = await fetch(`${environment.apiUrl}/ai/delete-result/`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${getToken()}`,
        },
        body: JSON.stringify({
          emr_type_id: id,
          key: deleteItem.key,
          value: deleteItem.value
        })
      });

      const data = await response.json();

      if (response.ok) {
        // Refresh the results after successful deletion
        await fetchResults();

        // Refresh EMR type data
        const updatedResponse = await fetch(`${environment.apiUrl}/emr-types/${id}`, {
          headers: {
            Authorization: `Bearer ${getToken()}`,
          },
        });

        if (updatedResponse.ok) {
          const updatedData = await updatedResponse.json();
          setEmrType(updatedData);
        }

        setShowDeleteConfirm(false);
        setDeleteItem(null);
        setShowDeleteSuccess(true);
        setTimeout(() => setShowDeleteSuccess(false), 3000); // Hide after 3 seconds
      } else {
        console.error('Delete failed:', data);
        alert(data.message || 'Failed to delete item');
      }
    } catch (error) {
      console.error('Error deleting item:', error);
      alert('Failed to delete item');
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteConfirm(false);
    setDeleteItem(null);
  };

  const handleDownloadFile = async (fileUrl, filename) => {
    try {
      // Check if URL is relative and needs API base URL
      let fullUrl = fileUrl;
      const isRelativeUrl = !fileUrl.startsWith('http://') && !fileUrl.startsWith('https://');

      if (isRelativeUrl) {
        // If it's a relative URL, prepend the API base URL
        fullUrl = fileUrl.startsWith('/')
          ? `${environment.apiUrl}${fileUrl}`
          : `${environment.apiUrl}/${fileUrl}`;
      }

      console.log('Downloading file from URL:', fullUrl);
      console.log('Original URL:', fileUrl);
      console.log('Filename:', filename);
      console.log('Is relative URL:', isRelativeUrl);

      // Build fetch options
      const fetchOptions = {
        method: 'GET',
      };

      // Only add Authorization header if it's a relative URL (API endpoint)
      // Full URLs (CDN, S3, etc.) might not need or accept auth headers
      if (isRelativeUrl) {
        fetchOptions.headers = {
          Authorization: `Bearer ${getToken()}`,
        };
      }

      const response = await fetch(fullUrl, fetchOptions);

      if (!response.ok) {
        const errorText = await response.text().catch(() => '');
        console.error('Download failed:', {
          status: response.status,
          statusText: response.statusText,
          errorText: errorText,
          url: fullUrl
        });
        throw new Error(`Failed to download file: ${response.status} ${response.statusText}`);
      }

      const blob = await response.blob();
      console.log('Blob created, size:', blob.size, 'type:', blob.type);

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename || 'download';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading file:', error);
      console.error('Error details:', {
        message: error.message,
        stack: error.stack,
        fileUrl: fileUrl
      });

      // Fallback: try using the download attribute directly
      // This might work if the browser can handle it natively
      try {
        console.log('Trying fallback download method...');
        const link = document.createElement('a');
        link.href = fileUrl;
        link.download = filename || 'download';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } catch (fallbackError) {
        console.error('Fallback download also failed:', fallbackError);
        alert(`Failed to download file: ${error.message}\n\nPlease check the browser console for more details or try using the "Open" link and save the file manually.`);
      }
    }
  };

  const handleFinalize = async () => {
    setFinalizeError("");
    setFinalizeLoading(true);
    try {
      const response = await fetch(`${environment.apiUrl}/emr-types/${id}/finalize`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getToken()}`,
        },
      });

      if (response.ok) {

        // Refresh the EMR type data to get updated status
        const updatedResponse = await fetch(`${environment.apiUrl}/emr-types/${id}`, {
          headers: {
            Authorization: `Bearer ${getToken()}`,
          },
        });

        if (updatedResponse.ok) {
          const updatedData = await updatedResponse.json();
          setEmrType(updatedData);
        }
      } else {
        const data = await response.json().catch(() => ({}));
        console.log("Finalize Error:", data);
        const errorMessage = data.detail || data.error || "Failed to finalize EMR type.";
        setFinalizeError(errorMessage);
        setShowFinalizeErrorModal(true);
      }
    } catch (err) {
      setFinalizeError("Network error");
      setShowFinalizeErrorModal(true);
    } finally {
      setFinalizeLoading(false);
    }
  };

  // Function to open instruction modal
  const openInstructionModal = (index, key, currentInstructions, value) => {
    setEditingIndex(index);
    setEditingKey(key);
    setEditingInstruction(currentInstructions || "");
    setEditingValue(value || "");
    setStatusDropdownOpen(false); // Close dropdown when opening modal
    setShowInstructionModal(true);
  };

  // Function to handle column sorting
  const handleSort = (columnKey) => {
    let direction = 'asc';

    if (sortConfig.key === columnKey) {
      if (sortConfig.direction === 'asc') {
        direction = 'desc';
      } else if (sortConfig.direction === 'desc') {
        // Remove sorting (return to original order)
        setSortConfig({ key: null, direction: 'asc' });
        return;
      }
    }

    setSortConfig({ key: columnKey, direction });
  };

  // Function to get sort indicator
  const getSortIndicator = (columnKey) => {
    if (sortConfig.key !== columnKey) {
      return (
        <svg className="w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
        </svg>
      );
    }

    if (sortConfig.direction === 'asc') {
      return (
        <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
        </svg>
      );
    }

    return (
      <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
      </svg>
    );
  };

  // Function to toggle grid expansion
  const toggleGridExpansion = () => {
    console.log('Before toggle - isGridExpanded:', isGridExpanded);
    const newState = !isGridExpanded;
    console.log('Setting isGridExpanded to:', newState);
    setIsGridExpanded(newState);
  };

  // Function to filter analysis results
  const filteredAnalysisResults = analysisResults && Array.isArray(analysisResults) ? analysisResults.filter(item => {
    // Text filter
    if (filterText) {
      const searchText = filterText.toLowerCase();
      const matchesText = (
        item.key?.toLowerCase().includes(searchText) ||
        item.value?.toLowerCase().includes(searchText) ||
        item.instructions?.toLowerCase().includes(searchText) ||
        item.status?.toLowerCase().includes(searchText)
      );
      if (!matchesText) return false;
    }

    // Status filter
    if (statusFilter) {
      const currentStatus = (item.status || (item.value && item.value !== 'Empty' ? 'found' : 'not found')).toLowerCase();
      if (currentStatus !== statusFilter.toLowerCase()) return false;
    }

    return true;
  }).sort((a, b) => {
    // Apply sorting if configured
    if (!sortConfig.key) return 0;

    let aValue = a[sortConfig.key];
    let bValue = b[sortConfig.key];

    // Handle different data types
    if (typeof aValue === 'string') {
      aValue = aValue.toLowerCase();
      bValue = bValue.toLowerCase();
    } else if (aValue instanceof Date && bValue instanceof Date) {
      aValue = aValue.getTime();
      bValue = bValue.getTime();
    } else if (typeof aValue === 'number' && typeof bValue === 'number') {
      // Numbers are already comparable
    } else {
      // Convert to string for comparison
      aValue = String(aValue || '').toLowerCase();
      bValue = String(bValue || '').toLowerCase();
    }

    if (aValue < bValue) {
      return sortConfig.direction === 'asc' ? -1 : 1;
    }
    if (aValue > bValue) {
      return sortConfig.direction === 'asc' ? 1 : -1;
    }
    return 0;
  }) : [];

  // Function to handle status editing
  const handleStatusClick = (index) => {
    setEditingStatusIndex(editingStatusIndex === index ? null : index);
  };

  // Function to select status option
  const selectStatus = async (index, newStatus) => {
    try {
      const item = filteredAnalysisResults[index];
      const response = await fetch(`${environment.apiUrl}/emr-types/${id}/status`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getToken()}`,
        },
        body: JSON.stringify({
          key: item.key,
          value: item.value,
          status: newStatus
        }),
      });

      if (response.ok) {
        // Refresh the analysis results to get updated data
        const getResponse = await fetch(`${environment.apiUrl}/emr-types/${id}/results`, {
          headers: {
            Authorization: `Bearer ${getToken()}`,
          },
        });

        if (getResponse.ok) {
          const updatedData = await getResponse.json();
          setAnalysisResults(updatedData);
          console.log(`Status updated successfully for ${item.key} to: ${newStatus}`);

          // Show success message
          setShowSuccessMessage(true);
          setTimeout(() => setShowSuccessMessage(false), 3000);
        }
      } else {
        const errorData = await response.json().catch(() => ({}));
        console.error("Failed to update status:", errorData);
      }
    } catch (err) {
      console.error("Network error updating status:", err);
    } finally {
      setEditingStatusIndex(null);
    }
  };

  // Handle row selection
  const handleRowSelect = (index) => {
    const newSelectedRows = new Set(selectedRows);
    if (newSelectedRows.has(index)) {
      newSelectedRows.delete(index);
    } else {
      newSelectedRows.add(index);
    }
    setSelectedRows(newSelectedRows);
    setSelectAll(newSelectedRows.size === filteredAnalysisResults.length);
  };

  // Handle select all
  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedRows(new Set());
      setSelectAll(false);
    } else {
      const allIndices = new Set(filteredAnalysisResults.map((_, index) => index));
      setSelectedRows(allIndices);
      setSelectAll(true);
    }
  };

  // Bulk update status
  const handleBulkStatusUpdate = async (newStatus) => {
    setBulkUpdateLoading(true);
    setBulkUpdateStatus(newStatus);
    try {
      const selectedItems = Array.from(selectedRows).map(index => filteredAnalysisResults[index]);

      // Update each selected item
      for (const item of selectedItems) {
        const response = await fetch(`${environment.apiUrl}/emr-types/${id}/status`, {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${getToken()}`,
          },
          body: JSON.stringify({
            key: item.key,
            value: item.value,
            status: newStatus
          }),
        });
      }

      // Refresh the analysis results to get updated data
      const getResponse = await fetch(`${environment.apiUrl}/emr-types/${id}/results`, {
        headers: {
          Authorization: `Bearer ${getToken()}`,
        },
      });

      if (getResponse.ok) {
        const updatedData = await getResponse.json();
        setAnalysisResults(updatedData);

        // Show success message
        setShowSuccessMessage(true);
        setTimeout(() => setShowSuccessMessage(false), 3000);
      }

      // Clear selection after bulk update
      setSelectedRows(new Set());
      setSelectAll(false);
      console.log(`Bulk status update completed for ${selectedItems.length} items to: ${newStatus}`);
    } catch (err) {
      console.error("Network error updating bulk status:", err);
    } finally {
      setBulkUpdateLoading(false);
      setBulkUpdateStatus(null);
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Don't close if clicking on the status button itself
      if (event.target.closest('.status-button')) {
        return;
      }

      // Don't close if clicking on the status filter button itself
      if (event.target.closest('.status-filter-button')) {
        return;
      }

      if (editingStatusIndex !== null) {
        setEditingStatusIndex(null);
      }

      if (statusDropdownOpen) {
        setStatusDropdownOpen(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [editingStatusIndex, statusDropdownOpen]);

  // Fetch manual fields on component mount and when tab changes
  useEffect(() => {
    if (activeTab === "manual" && id) {
      fetchManualFields();
      fetchEmrTypeFields(); // Also fetch EMR Type Fields for lookup
    }
  }, [activeTab, id]);

  // Function to fetch manual fields
  const fetchManualFields = async () => {
    setLoadingManualFields(true);
    setManualFieldError("");
    try {
      const response = await fetch(`${environment.apiUrl}/manual-fields/emr-type/${id}`, {
        headers: {
          Authorization: `Bearer ${getToken()}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setManualFields(data);
      } else {
        const errorData = await response.json().catch(() => ({}));
        setManualFieldError(errorData.detail || "Failed to fetch manual fields");
      }
    } catch (err) {
      setManualFieldError("Network error fetching manual fields");
    } finally {
      setLoadingManualFields(false);
    }
  };

  // Function to fetch EMR Type Fields for lookup
  const fetchEmrTypeFields = async () => {
    setLoadingEmrTypeFields(true);
    setEmrTypeFieldsError("");
    try {
      const response = await fetch(`${environment.apiUrl}/emr-types-fields/`, {
        headers: {
          Authorization: `Bearer ${getToken()}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        console.log('EMR Type Fields fetched for lookup:', data);
        // Only show fields where analyzable = 'not_for_analyzing'
        const notForAnalyzingFields = data.filter(field => field.analyzable === 'not_for_analyzing');
        console.log('Filtered not_for_analyzing fields:', notForAnalyzingFields);
        setEmrTypeFields(notForAnalyzingFields);
      } else {
        const errorData = await response.json().catch(() => ({}));
        setEmrTypeFieldsError(errorData.detail || "Failed to fetch EMR type fields");
      }
    } catch (err) {
      setEmrTypeFieldsError("Network error fetching EMR type fields");
    } finally {
      setLoadingEmrTypeFields(false);
    }
  };

  // Function to create manual field
  const createManualField = async () => {
    if (!selectedLookupField) {
      setManualFieldError("You must select a field from the dropdown");
      return;
    }

    setCreatingManualField(true);
    setManualFieldError("");
    try {
      const response = await fetch(`${environment.apiUrl}/manual-fields/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getToken()}`,
        },
        body: JSON.stringify({
          name: selectedLookupField.name,
          emr_type_id: id
        }),
      });

      if (response.ok) {
        const data = await response.json();
        setManualFields(prev => [...prev, data]);
        setSelectedLookupField(null);
        setShowCreateManualFieldModal(false);
      } else {
        const errorData = await response.json().catch(() => ({}));
        setManualFieldError(errorData.detail || "Failed to create manual field");
      }
    } catch (err) {
      setManualFieldError("Network error creating manual field");
    } finally {
      setCreatingManualField(false);
    }
  };

  // Function to update manual field
  const updateManualField = async () => {
    if (!editingManualFieldName.trim()) {
      setManualFieldError("Field name is required");
      return;
    }

    setUpdatingManualField(true);
    setManualFieldError("");
    try {
      // When editing, we need to use the original manual field ID, not the selected EMR type field ID
      // The originalManualFieldId is stored when opening the edit modal
      let manualFieldId;

      if (originalManualFieldId) {
        // We're editing an existing manual field - use the stored original ID
        manualFieldId = originalManualFieldId;
        console.log('Editing existing manual field with ID:', manualFieldId);
      } else {
        // We're creating a new manual field
        manualFieldId = editingManualField?.id;
        console.log('Creating new manual field with ID:', manualFieldId);
      }

      if (!manualFieldId) {
        setManualFieldError("Invalid manual field ID");
        return;
      }

      const response = await fetch(`${environment.apiUrl}/manual-fields/${manualFieldId}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getToken()}`,
        },
        body: JSON.stringify({
          name: editingManualFieldName.trim()
        }),
      });

      if (response.ok) {
        const data = await response.json();
        setManualFields(prev => prev.map(field =>
          field.id === manualFieldId ? data : field
        ));
        setEditingManualFieldName("");
        setEditingManualField(null);
        setOriginalManualFieldId(null);
        setShowEditManualFieldModal(false);
      } else {
        const errorData = await response.json().catch(() => ({}));
        setManualFieldError(errorData.detail || "Failed to update manual field");
      }
    } catch (err) {
      setManualFieldError("Network error updating manual field");
    } finally {
      setUpdatingManualField(false);
    }
  };

  // Function to show delete confirmation modal
  const showDeleteConfirmation = (field) => {
    setFieldToDelete(field);
    setShowDeleteManualFieldModal(true);
  };

  // Function to delete manual field
  const deleteManualField = async () => {
    if (!fieldToDelete) return;

    setDeletingManualField(true);
    setManualFieldError("");
    try {
      const response = await fetch(`${environment.apiUrl}/manual-fields/${fieldToDelete.id}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${getToken()}`,
        },
      });

      if (response.ok) {
        setManualFields(prev => prev.filter(field => field.id !== fieldToDelete.id));
        setShowDeleteManualFieldModal(false);
        setFieldToDelete(null);
      } else {
        const errorData = await response.json().catch(() => ({}));
        setManualFieldError(errorData.detail || "Failed to delete manual field");
      }
    } catch (err) {
      setManualFieldError("Network error deleting manual field");
    } finally {
      setDeletingManualField(false);
    }
  };

  // Function to open edit modal
  const openEditModal = (field) => {
    // When editing an existing manual field, we need to find the corresponding EMR type field
    // to show in the dropdown, while preserving the original manual field data
    console.log('Opening edit modal for manual field:', field);

    // Find the corresponding EMR type field by name
    const correspondingEmrField = emrTypeFields.find(emrField =>
      emrField.name === field.name
    );

    if (correspondingEmrField) {
      // Set the EMR type field in the dropdown, but preserve the original manual field data
      setEditingManualField(correspondingEmrField);
      setEditingManualFieldName(field.name);
      setOriginalManualFieldId(field.id); // Store the original manual field ID
      console.log('Found corresponding EMR field:', correspondingEmrField.name, 'Stored original manual field ID:', field.id);
    } else {
      // Fallback: just set the manual field directly
      setEditingManualField(field);
      setEditingManualFieldName(field.name);
      setOriginalManualFieldId(field.id);
      console.log('No corresponding EMR field found, using manual field directly');
    }

    setShowEditManualFieldModal(true);
  };



  // Function to save instruction from modal
  const saveModalInstruction = async () => {
    setSavingModalInstruction(true);
    try {
      const response = await fetch(`${environment.apiUrl}/emr-types/${id}/instructions`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getToken()}`,
        },
        body: JSON.stringify({
          key: editingKey,
          value: editingValue,
          instructions: editingInstruction
        }),
      });

      if (response.ok) {
        // Auto-call GET to refresh instructions
        const getResponse = await fetch(`${environment.apiUrl}/emr-types/${id}/results?instructions_only=true`, {
          headers: {
            Authorization: `Bearer ${getToken()}`,
          },
        });

        if (getResponse.ok) {
          const updatedData = await getResponse.json();
          // Update the analysis results with new instructions
          const updatedResults = analysisResults.map(result => {
            const updated = updatedData.find(item => item.key === result.key && item.value === result.value);
            return updated ? { ...result, instructions: updated.instructions } : result;
          });
          setAnalysisResults(updatedResults);
        }

        // Refresh EMR type data to get updated status
        const updatedResponse = await fetch(`${environment.apiUrl}/emr-types/${id}`, {
          headers: {
            Authorization: `Bearer ${getToken()}`,
          },
        });

        if (updatedResponse.ok) {
          const updatedData = await updatedResponse.json();
          setEmrType(updatedData);
        }

        // Close the modal
        setShowInstructionModal(false);
        setEditingInstruction("");
        setEditingIndex(null);
        setEditingKey("");
        setEditingValue("");
      } else {
        console.error("Failed to save instructions");
      }
    } catch (err) {
      console.error("Error saving instructions:", err);
    } finally {
      setSavingModalInstruction(false);
    }
  };

  // Function to save session instructions
  const saveSessionInstructions = async () => {
    if (!methodsInstructions.trim() && !progressTowardsGoalInstructions.trim() && !recommendedChangesInstructions.trim()) {
      setSessionInstructionsError("At least one section must have content");
      return;
    }

    setSavingSessionInstructions(true);
    setSessionInstructionsError("");

    // Combine all sections into full session instructions with clean formatting
    const fullSessionInstructions = `Section 1: Field "Methods"
${methodsInstructions.trim()}

Section 2: Field "Progress_towards_goal"
${progressTowardsGoalInstructions.trim()}

Section 3: Field "Recommended_changes"
${recommendedChangesInstructions.trim()}`;

    // Prepare the individual field data with complete sections including headers
    // methods_instructions should contain topHeaderText + section1Header + methodsInstructions
    const methodsInstructionsComplete = (topHeaderText.trim() + "\n\n" + section1Header + "\n" + methodsInstructions.trim()).trim();
    const progressTowardsGoalInstructionsComplete = (section2Header + "\n" + progressTowardsGoalInstructions.trim()).trim();
    const recommendedChangesInstructionsComplete = (section3Header + "\n" + recommendedChangesInstructions.trim()).trim();

    try {
      const response = await fetch(`${environment.apiUrl}/emr-types/save-session-instructions/`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getToken()}`,
        },
        body: JSON.stringify({
          emr_type_id: id,
          session_instructions: fullSessionInstructions,
          methods_instructions: methodsInstructionsComplete,
          progress_towards_goal_instructions: progressTowardsGoalInstructionsComplete,
          recommended_changes_instructions: recommendedChangesInstructionsComplete
        }),
      });

      if (response.ok) {
        const data = await response.json();
        console.log("Session instructions saved successfully:", data);

        // Update the EMR type with new session instructions
        setEmrType(prev => ({
          ...prev,
          session_instructions: fullSessionInstructions
        }));

        // Close the modal
        setShowSessionInstructionsModal(false);
        setSessionInstructions("");
        setMethodsInstructions("");
        setProgressTowardsGoalInstructions("");
        setRecommendedChangesInstructions("");

        // Show success message with actual backend response
        setSessionInstructionsSuccess(data.message || "Session instructions saved successfully");
        setShowSessionInstructionsSuccess(true);
        setTimeout(() => setShowSessionInstructionsSuccess(false), 3000);
      } else {
        const errorData = await response.json().catch(() => ({}));
        setSessionInstructionsError(errorData.detail || "Failed to save session instructions");
      }
    } catch (err) {
      setSessionInstructionsError("Network error saving session instructions");
    } finally {
      setSavingSessionInstructions(false);
    }
  };

  if (loading) {
    return <div className="text-center text-slate-500 py-12">Loading EMR type...</div>;
  }
  if (error) {
    return <div className="text-center text-red-500 py-12">{typeof error === 'string' ? error : JSON.stringify(error)}</div>;
  }
  if (!emrType) {
    return <div className="text-center text-slate-500 py-12">EMR type not found.</div>;
  }

  return (
    <div className="w-full max-w-7xl mx-auto p-6 mt-6">
      <div className="flex-1 min-w-0">
        <button
          className="flex items-center gap-2 text-blue-600 hover:text-blue-800 mb-6"
          onClick={async () => {
            // If grid chunks are displayed, call API first, then navigate
            if (chunkResponse && chunkResponse.all_chunks) {
              try {
                console.log("Grid chunks detected, calling API before navigation...");
                const response = await fetch(`${environment.apiUrl}/emr-types/${id}/back-action`, {
                  method: "PUT",
                  headers: {
                    Authorization: `Bearer ${getToken()}`,
                  },
                });

                if (response.ok) {
                  console.log("API call successful before navigation");
                } else {
                  console.log("API call failed, but continuing with navigation");
                }
              } catch (error) {
                console.log("API call error, but continuing with navigation:", error);
              }
            }

            // If analysis is in progress (progress bar is showing), call API before navigation
            if (analyzeProgress && analyzeProgress.progress < 100) {
              try {
                console.log("Analysis in progress detected, calling API before navigation...");
                const response = await fetch(`${environment.apiUrl}/emr-types/${id}/back-action`, {
                  method: "PUT",
                  headers: {
                    Authorization: `Bearer ${getToken()}`,
                  },
                });

                if (response.ok) {
                  console.log("Back action API call successful for in-progress analysis");
                } else {
                  console.log("Back action API call failed, but continuing with navigation");
                }
              } catch (error) {
                console.log("Back action API call error, but continuing with navigation:", error);
              }
            }

            // If analysis is loading but progress hasn't started yet, also call API
            if (analyzeLoading && (!analyzeProgress || analyzeProgress.progress === 0)) {
              try {
                console.log("Analysis loading detected, calling API before navigation...");
                const response = await fetch(`${environment.apiUrl}/emr-types/${id}/back-action`, {
                  method: "PUT",
                  headers: {
                    Authorization: `Bearer ${getToken()}`,
                  },
                });

                if (response.ok) {
                  console.log("Back action API call successful for loading analysis");
                } else {
                  console.log("Back action API call failed, but continuing with navigation");
                }
              } catch (error) {
                console.log("Back action API call error, but continuing with navigation:", error);
              }
            }

            // If analysis is complete but status is still "processing" (user hasn't confirmed results yet)
            if (emrType.status === "processing" && (!analyzeProgress || analyzeProgress?.progress >= 100)) {
              try {
                console.log("Analysis complete but status still processing, calling API before navigation...");
                const response = await fetch(`${environment.apiUrl}/emr-types/${id}/back-action`, {
                  method: "PUT",
                  headers: {
                    Authorization: `Bearer ${getToken()}`,
                  },
                });

                if (response.ok) {
                  console.log("Back action API call successful for completed but unconfirmed analysis");
                } else {
                  console.log("Back action API call failed, but continuing with navigation");
                }
              } catch (error) {
                console.log("Back action API call error, but continuing with navigation:", error);
              }
            }

            // If generate response is in progress
            if (loadingReport) {
              try {
                console.log("Generate response in progress, calling API before navigation...");
                const response = await fetch(`${environment.apiUrl}/emr-types/${id}/back-action`, {
                  method: "PUT",
                  headers: {
                    Authorization: `Bearer ${getToken()}`,
                  },
                });

                if (response.ok) {
                  console.log("Back action API call successful for generate response");
                } else {
                  console.log("Back action API call failed, but continuing with navigation");
                }
              } catch (error) {
                console.log("Back action API call error, but continuing with navigation:", error);
              }
            }

            // If finalize is in progress
            if (finalizeLoading) {
              try {
                console.log("Finalize in progress, calling API before navigation...");
                const response = await fetch(`${environment.apiUrl}/emr-types/${id}/back-action`, {
                  method: "PUT",
                  headers: {
                    Authorization: `Bearer ${getToken()}`,
                  },
                });

                if (response.ok) {
                  console.log("Back action API call successful for finalize");
                } else {
                  console.log("Back action API call failed, but continuing with navigation");
                }
              } catch (error) {
                console.log("Back action API call error, but continuing with navigation:", error);
              }
            }

            // Always navigate back
            navigate(-1);
          }}
        >
          <ArrowLeftIcon className="w-5 h-5" /> Back
        </button>
        <div className="mb-6 rounded-xl border border-slate-200 bg-white/80 p-4">
          <div className="flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
            <h2 className="text-2xl font-bold text-slate-800">EMR Type Details</h2>
            <div className="flex flex-wrap items-center gap-2 xl:justify-end">
            {/* Status Badge - Left */}
            <div className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-lg px-3 py-1.5">
              <div className="flex items-center gap-1.5">
                <div className={`w-1.5 h-1.5 rounded-full ${(emrType.status || '').toLowerCase() === 'draft' ? 'bg-gray-500' :
                  (emrType.status || '').toLowerCase() === 'analyzed' ? 'bg-blue-500' :
                    (emrType.status || '').toLowerCase() === 'generated' ? 'bg-purple-500' :
                      (emrType.status || '').toLowerCase() === 'active' ? 'bg-green-500' :
                        'bg-slate-400'
                  }`}></div>
                <span className="text-xs font-medium text-slate-700">Status</span>
              </div>
              <div className={`px-2 py-0.5 rounded-full text-xs font-semibold shadow-sm ${(emrType.status || '').toLowerCase() === 'draft' ? 'bg-gray-100 text-gray-700 border border-gray-300' :
                (emrType.status || '').toLowerCase() === 'analyzed' ? 'bg-blue-100 text-blue-700 border border-blue-300' :
                  (emrType.status || '').toLowerCase() === 'generated' ? 'bg-purple-100 text-purple-700 border border-purple-300' :
                    (emrType.status || '').toLowerCase() === 'active' ? 'bg-green-100 text-green-700 border border-green-300' :
                      'bg-slate-100 text-slate-700 border border-slate-300'
                }`}>
                {(emrType.status || 'draft').charAt(0).toUpperCase() + (emrType.status || 'draft').slice(1).toLowerCase()}
              </div>
            </div>

            {/* Analyze Button - Middle */}
            <button
              className={`px-4 py-1.5 rounded-lg transition-colors text-sm font-semibold flex items-center gap-2 ${analyzeLoading
                ? "bg-blue-100 text-blue-700 border border-blue-300"
                : "bg-blue-600 text-white hover:bg-blue-700"
                }`}
              onClick={handleAnalyze}
              disabled={analyzeLoading}
            >
              {analyzeLoading ? (
                <>
                  <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Analyzing...
                </>
              ) : (
                "Analyze"
              )}
            </button>

            {/* Generate Response Button - Right */}
            <button
              className={`px-4 py-1.5 rounded-lg transition-colors text-sm font-semibold flex items-center gap-2 ${loadingReport
                ? "bg-blue-100 text-blue-700 border border-blue-300"
                : "bg-blue-600 text-white hover:bg-blue-700"
                }`}
              onClick={handleGenerateResponse}
              disabled={loadingReport}
            >
              {loadingReport ? (
                <>
                  <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Generating...
                </>
              ) : (
                "Generate Response"
              )}
            </button>

            {/* Finalized Button - New */}
            <button
              className={`px-4 py-1.5 rounded-lg transition-colors text-sm font-semibold flex items-center gap-2 ${finalizeLoading
                ? "bg-blue-100 text-blue-700 border border-blue-300"
                : "bg-blue-600 text-white hover:bg-blue-700"
                }`}
              onClick={handleFinalize}
              disabled={finalizeLoading}
            >
              {finalizeLoading ? (
                <>
                  <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Finalizing...
                </>
              ) : (
                "Finalized"
              )}
            </button>

            {/* Cancel Button - Only show when processing */}
            {(emrType.status || '').toLowerCase() === 'processing' && (
              <button
                className={`px-4 py-1.5 rounded-lg transition-colors text-sm font-semibold flex items-center gap-2 ${cancelLoading ? 'bg-red-400 cursor-not-allowed' : 'bg-red-600 hover:bg-red-700'} text-white`}
                disabled={cancelLoading}
                onClick={async () => {
                  if (cancelLoading) return; // Prevent multiple clicks
                  
                  setCancelLoading(true);
                  
                  // Stop progress polling immediately
                  if (progressInterval) {
                    clearInterval(progressInterval);
                    setProgressInterval(null);
                  }
                  
                  // Same logic as back button - call back-action API then navigate
                  try {
                    console.log("Cancel button clicked, calling back-action API...");
                    const response = await fetch(`${environment.apiUrl}/emr-types/${id}/back-action`, {
                      method: "PUT",
                      headers: {
                        Authorization: `Bearer ${getToken()}`,
                      },
                    });

                    if (response.ok) {
                      console.log("Back action API call successful from Cancel button");
                    } else {
                      console.log("Back action API call failed, but continuing with navigation");
                    }
                  } catch (error) {
                    console.log("Back action API call error, but continuing with navigation:", error);
                  }

                  // Navigate back
                  navigate(-1);
                }}
              >
                {cancelLoading ? (
                  <>
                    <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Cancelling...
                  </>
                ) : (
                  'Cancel'
                )}
              </button>
            )}
            </div>
          </div>
        </div>

        {/* Details row: Name, Session Type, Documentation Methods, User */}
        <div className="rounded-xl border border-slate-200 bg-white/80 p-4 mb-6 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-x-6 gap-y-4">
            <div>
              <div className="text-xs text-slate-500 font-semibold mb-1">Name</div>
              <div className="text-lg font-semibold text-slate-900">{emrType.name}</div>
            </div>
            <div>
              <div className="text-xs text-slate-500 font-semibold mb-1">Session Type</div>
              <div className="text-base text-slate-800">{emrType.session_type || <span className="text-slate-400">—</span>}</div>
            </div>
            <div>
              <div className="text-xs text-slate-500 font-semibold mb-1">Documentation Methods</div>
              <div className="text-base text-slate-800 whitespace-pre-line">
                {emrType.documentation_method_id
                  ? (documentationMethods.length > 0
                    ? documentationMethods.find(method => method.id === emrType.documentation_method_id)?.name || emrType.documentation_method_id
                    : emrType.documentation_method_id)
                  : <span className="text-slate-400">—</span>}
              </div>
            </div>
            <div>
              <div className="text-xs text-slate-500 font-semibold mb-1">User</div>
              {loadingCreator ? (
                <div className="text-sm text-slate-400">Loading...</div>
              ) : creatorUser ? (
                <div className="text-base text-slate-800">
                  <div className="font-medium text-slate-900">{creatorUser.full_name || creatorUser.name || "—"}</div>
                  <div className="text-sm text-slate-600">{creatorUser.email || "—"}</div>
                </div>
              ) : (
                <div className="text-base text-slate-400">—</div>
              )}
            </div>
          </div>

          {/* Files, Created, Updated row */}
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-x-6 gap-y-4 items-start">
            <div className="lg:col-span-2">
              <div className="text-xs text-slate-500 font-semibold mb-1">Files</div>
              {emrType.files && emrType.files.length > 0 ? (
                <div className="flex flex-col gap-2 mt-1">
                  {emrType.files.map((file, idx) => (
                    <div key={idx} className="flex flex-wrap items-center gap-2 bg-blue-50 text-blue-700 px-3 py-2 rounded text-sm">
                      <PaperClipIcon className="w-4 h-4" />
                      <span className="truncate max-w-xs">{file.filename || file.name || `File ${idx + 1}`}</span>
                      {file.url && (
                        <div className="ml-2 flex gap-3">
                          <a
                            href={file.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="underline text-blue-600 hover:text-blue-800 cursor-pointer"
                          >
                            Open
                          </a>
                          <button
                            onClick={(e) => {
                              e.preventDefault();
                              handleDownloadFile(file.url, file.filename || file.name || `File ${idx + 1}`);
                            }}
                            className="underline text-blue-600 hover:text-blue-800 cursor-pointer bg-transparent border-none p-0"
                          >
                            Download
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-slate-400 text-sm">No files</div>
              )}
            </div>

            <div className="lg:col-span-1">
              <div className="text-xs text-slate-500 font-semibold mb-1">Created</div>
              <div className="text-base text-slate-800">{emrType.created_at ? new Date(emrType.created_at).toLocaleString() : "—"}</div>
            </div>

            <div className="lg:col-span-1">
              <div className="text-xs text-slate-500 font-semibold mb-1">Updated</div>
              <div className="text-base text-slate-800">{emrType.updated_at ? new Date(emrType.updated_at).toLocaleString() : "—"}</div>
            </div>
          </div>
        </div>

        {/* Main content: Tabbed Interface */}
        <div className="flex flex-col h-full w-full">
          {/* Tab Navigation */}
          <div className="flex border-b border-slate-200 mb-6 items-center justify-between">
            <div className="flex">
              <button
                onClick={() => setActiveTab("analysis")}
                className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${activeTab === "analysis"
                  ? "border-blue-500 text-blue-600"
                  : "border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300"
                  }`}
              >
                Analysis Results
              </button>
              <button
                onClick={() => setActiveTab("manual")}
                className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${activeTab === "manual"
                  ? "border-blue-500 text-blue-600"
                  : "border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300"
                  }`}
              >
                Manual Fields
              </button>
            </div>

            {/* Instructions Button - positioned on the right side of the tabs */}
            <button
              onClick={async () => {
                console.log('=== BUTTON CLICKED ===');
                console.log('Current emrType:', emrType);
                console.log('Current emrType.session_instructions:', emrType.session_instructions);

                // Refresh EMR type data to get latest session instructions from database
                console.log('=== MAKING API CALL ===');
                console.log('API URL:', `${environment.apiUrl}/emr-types/${id}`);
                try {
                  const response = await fetch(`${environment.apiUrl}/emr-types/${id}`, {
                    headers: {
                      Authorization: `Bearer ${getToken()}`,
                    },
                  });

                  if (response.ok) {
                    const updatedEmrType = await response.json();
                    console.log('Fresh data from API:', updatedEmrType);
                    console.log('Fresh session_instructions:', updatedEmrType.session_instructions);

                    setEmrType(updatedEmrType);

                    // NEW PARSING APPROACH: Organize content based on section headers
                    console.log('=== NEW PARSING APPROACH ===');

                    // Parse methods_instructions field for top content and Section 1
                    if (updatedEmrType.methods_instructions) {
                      const section1Pattern = 'Section 1: Field "Methods"';
                      const lastSection1Index = updatedEmrType.methods_instructions.lastIndexOf(section1Pattern);

                      console.log('Last Section 1 index:', lastSection1Index);

                      if (lastSection1Index !== -1) {
                        // Top content: everything until the last "Section 1: Field 'Methods'" (NOT including it)
                        const topContent = updatedEmrType.methods_instructions.substring(0, lastSection1Index).trim();

                        // Section 1 header: the last "Section 1: Field 'Methods'" occurrence
                        const section1HeaderText = section1Pattern;

                        // Section 1 content: everything after the last "Section 1: Field 'Methods'"
                        const methodsContent = updatedEmrType.methods_instructions.substring(lastSection1Index + section1Pattern.length).trim();

                        console.log('Top content length:', topContent.length);
                        console.log('Section 1 header:', section1HeaderText);
                        console.log('Methods content length:', methodsContent.length);

                        setTopHeaderText(topContent);
                        setSection1Header(section1HeaderText);
                        setMethodsInstructions(methodsContent);
                      } else {
                        // No Section 1 found, use everything as top content
                        setTopHeaderText(updatedEmrType.methods_instructions);
                        setMethodsInstructions("");
                      }
                    }

                    // Parse progress_towards_goal_instructions field for Section 2
                    if (updatedEmrType.progress_towards_goal_instructions) {
                      const section2Pattern = 'Section 2: Field "Progress_towards_goal"';
                      const section2Index = updatedEmrType.progress_towards_goal_instructions.indexOf(section2Pattern);

                      if (section2Index !== -1) {
                        // Section 2 header: "Section 2: Field 'Progress_towards_goal'"
                        const section2HeaderText = section2Pattern;

                        // Section 2 content: everything after the header
                        const progressContent = updatedEmrType.progress_towards_goal_instructions.substring(section2Index + section2Pattern.length).trim();

                        setSection2Header(section2HeaderText);
                        setProgressTowardsGoalInstructions(progressContent);
                      } else {
                        // No header found, use everything as content
                        setProgressTowardsGoalInstructions(updatedEmrType.progress_towards_goal_instructions);
                      }
                    }

                    // Parse recommended_changes_instructions field for Section 3
                    if (updatedEmrType.recommended_changes_instructions) {
                      const section3Pattern = 'Section 3: Field "Recommended_changes"';
                      const section3Index = updatedEmrType.recommended_changes_instructions.indexOf(section3Pattern);

                      if (section3Index !== -1) {
                        // Section 3 header: "Section 3: Field 'Recommended_changes'"
                        const section3HeaderText = section3Pattern;

                        // Section 3 content: everything after the header
                        const recommendedContent = updatedEmrType.recommended_changes_instructions.substring(section3Index + section3Pattern.length).trim();

                        setSection3Header(section3HeaderText);
                        setRecommendedChangesInstructions(recommendedContent);
                      } else {
                        // No header found, use everything as content
                        setRecommendedChangesInstructions(updatedEmrType.recommended_changes_instructions);
                      }
                    }

                    console.log('=== END DEBUG ===');
                  } else {
                    console.log('API refresh failed, using current data');
                    // Parse current data using the same approach

                    // Parse methods_instructions field for top content and Section 1
                    if (emrType.methods_instructions) {
                      const section1Pattern = 'Section 1: Field "Methods"';
                      const lastSection1Index = emrType.methods_instructions.lastIndexOf(section1Pattern);

                      if (lastSection1Index !== -1) {
                        // Top content: everything until the last "Section 1: Field 'Methods'" (NOT including it)
                        const topContent = emrType.methods_instructions.substring(0, lastSection1Index).trim();

                        // Section 1 header: the last "Section 1: Field 'Methods'" occurrence
                        const section1HeaderText = section1Pattern;

                        // Section 1 content: everything after the last "Section 1: Field 'Methods'"
                        const methodsContent = emrType.methods_instructions.substring(lastSection1Index + section1Pattern.length).trim();

                        setTopHeaderText(topContent);
                        setSection1Header(section1HeaderText);
                        setMethodsInstructions(methodsContent);
                      } else {
                        // No Section 1 found, use everything as top content
                        setTopHeaderText(emrType.methods_instructions);
                        setMethodsInstructions("");
                      }
                    }

                    // Parse progress_towards_goal_instructions field for Section 2
                    if (emrType.progress_towards_goal_instructions) {
                      const section2Pattern = 'Section 2: Field "Progress_towards_goal"';
                      const section2Index = emrType.progress_towards_goal_instructions.indexOf(section2Pattern);

                      if (section2Index !== -1) {
                        // Section 2 header: "Section 2: Field 'Progress_towards_goal'"
                        const section2HeaderText = section2Pattern;

                        // Section 2 content: everything after the header
                        const progressContent = emrType.progress_towards_goal_instructions.substring(section2Index + section2Pattern.length).trim();

                        setSection2Header(section2HeaderText);
                        setProgressTowardsGoalInstructions(progressContent);
                      } else {
                        // No header found, use everything as content
                        setProgressTowardsGoalInstructions(emrType.progress_towards_goal_instructions);
                      }
                    }

                    // Parse recommended_changes_instructions field for Section 3
                    if (emrType.recommended_changes_instructions) {
                      const section3Pattern = 'Section 3: Field "Recommended_changes"';
                      const section3Index = emrType.recommended_changes_instructions.indexOf(section3Pattern);

                      if (section3Index !== -1) {
                        // Section 3 header: "Section 3: Field 'Recommended_changes'"
                        const section3HeaderText = section3Pattern;

                        // Section 3 content: everything after the header
                        const recommendedContent = emrType.recommended_changes_instructions.substring(section3Index + section3Pattern.length).trim();

                        setSection3Header(section3HeaderText);
                        setRecommendedChangesInstructions(recommendedContent);
                      } else {
                        // No header found, use everything as content
                        setRecommendedChangesInstructions(emrType.recommended_changes_instructions);
                      }
                    }
                  }
                } catch (err) {
                  console.log('API refresh error:', err);
                  // Parse current data using the same approach

                  // Parse methods_instructions field for top content and Section 1
                  if (emrType.methods_instructions) {
                    const section1Pattern = 'Section 1: Field "Methods"';
                    const lastSection1Index = emrType.methods_instructions.lastIndexOf(section1Pattern);

                    if (lastSection1Index !== -1) {
                      // Top content: everything until the last "Section 1: Field 'Methods'" (NOT including it)
                      const topContent = emrType.methods_instructions.substring(0, lastSection1Index).trim();

                      // Section 1 header: the last "Section 1: Field 'Methods'" occurrence
                      const section1HeaderText = section1Pattern;

                      // Section 1 content: everything after the last "Section 1: Field 'Methods'"
                      const methodsContent = emrType.methods_instructions.substring(lastSection1Index + section1Pattern.length).trim();

                      setTopHeaderText(topContent);
                      setSection1Header(section1HeaderText);
                      setMethodsInstructions(methodsContent);
                    } else {
                      // No Section 1 found, use everything as top content
                      setTopHeaderText(emrType.methods_instructions);
                      setMethodsInstructions("");
                    }
                  }

                  // Parse progress_towards_goal_instructions field for Section 2
                  if (emrType.progress_towards_goal_instructions) {
                    const section2Pattern = 'Section 2: Field "Progress_towards_goal"';
                    const section2Index = emrType.progress_towards_goal_instructions.indexOf(section2Pattern);

                    if (section2Index !== -1) {
                      // Section 2 header: "Section 2: Field 'Progress_towards_goal'"
                      const section2HeaderText = section2Pattern;

                      // Section 2 content: everything after the header
                      const progressContent = emrType.progress_towards_goal_instructions.substring(section2Index + section2Pattern.length).trim();

                      setSection2Header(section2HeaderText);
                      setProgressTowardsGoalInstructions(progressContent);
                    } else {
                      // No header found, use everything as content
                      setProgressTowardsGoalInstructions(emrType.progress_towards_goal_instructions);
                    }
                  }

                  // Parse recommended_changes_instructions field for Section 3
                  if (emrType.recommended_changes_instructions) {
                    const section3Pattern = 'Section 3: Field "Recommended_changes"';
                    const section3Index = emrType.recommended_changes_instructions.indexOf(section3Pattern);

                    if (section3Index !== -1) {
                      // Section 3 header: "Section 3: Field 'Recommended_changes'"
                      const section3HeaderText = section3Pattern;

                      // Section 3 content: everything after the header
                      const recommendedContent = emrType.recommended_changes_instructions.substring(section3Index + section3Pattern.length).trim();

                      setSection3Header(section3HeaderText);
                      setRecommendedChangesInstructions(recommendedContent);
                    } else {
                      // No header found, use everything as content
                      setRecommendedChangesInstructions(emrType.recommended_changes_instructions);
                    }
                  }
                }

                setSessionInstructionsError("");
                setShowSessionInstructionsModal(true);
              }}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
              {emrType.session_instructions ? "Edit Session Instructions" : "Edit Session Instructions"}
            </button>
          </div>



          {/* Analysis Results Tab Content */}
          {activeTab === "analysis" && (
            <>
              {analyzeError && !showAnalyzeErrorModal && (
                <div className="text-red-600 text-sm mb-4">{typeof analyzeError === 'string' ? analyzeError : JSON.stringify(analyzeError)}</div>
              )}
              {loadingResults && (
                <div className="text-slate-500 text-sm mb-4">Loading results...</div>
              )}

              {/* Progress Bar Section */}
              {analyzeProgress && !analyzeError && userStartedAnalysis && (
                <div className="mb-6 mt-8">
                  {/* Progress Bar */}
                  <div className="w-full bg-blue-200 rounded-full h-12 mb-3 border-2 border-blue-300 shadow-inner p-2 flex items-center">
                    <div
                      className="h-8 rounded-full transition-all duration-300 ease-out relative"
                      style={{
                        width: `${analyzeProgress.progress}%`,
                        background: `linear-gradient(90deg, hsl(220, 80%, 60%) 0%, hsl(220, 70%, 40%) 50%, hsl(220, 60%, 30%) 100%)`
                      }}
                    >
                      {/* Much bigger dark blue indicator on the right edge */}
                      <div
                        className="absolute right-0 top-1/2 transform -translate-y-1/2 w-8 h-8 bg-blue-700 rounded-full shadow-sm"
                        style={{ right: analyzeProgress.progress === 100 ? '0px' : '-10px' }}
                      ></div>
                    </div>
                  </div>

                  {/* Progress Message */}
                  <div className="text-sm text-slate-600 text-center">
                    {analyzeProgress.message}
                  </div>
                </div>
              )}

              {/* Analysis Results Section - Only show when chunks are not available */}
              {analysisResults && Array.isArray(analysisResults) && !chunkResponse && (
                <div className="mb-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-4">
                      <h3 className="text-lg font-semibold text-slate-700">Analysis Results</h3>
                      {selectedRows.size > 0 && (
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-slate-600 bg-slate-100 px-2 py-1 rounded">
                            {selectedRows.size} selected
                          </span>
                          <button
                            onClick={() => handleBulkStatusUpdate('confirmed')}
                            disabled={bulkUpdateLoading}
                            className={`px-3 py-1 text-xs bg-purple-100 text-purple-800 border border-purple-300 rounded-lg transition-colors font-medium ${bulkUpdateLoading ? 'opacity-50 cursor-not-allowed' : 'hover:bg-purple-200'
                              }`}
                          >
                            {bulkUpdateLoading && bulkUpdateStatus === 'confirmed' ? (
                              <span className="flex items-center gap-1">
                                <svg className="animate-spin h-3 w-3" fill="none" viewBox="0 0 24 24">
                                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                Updating...
                              </span>
                            ) : (
                              'Mark Confirmed'
                            )}
                          </button>
                          <button
                            onClick={() => handleBulkStatusUpdate('ignore')}
                            disabled={bulkUpdateLoading}
                            className={`px-3 py-1 text-xs bg-gray-100 text-gray-800 border border-gray-300 rounded-lg transition-colors font-medium ${bulkUpdateLoading ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-200'
                              }`}
                          >
                            {bulkUpdateLoading && bulkUpdateStatus === 'ignore' ? (
                              <span className="flex items-center gap-1">
                                <svg className="animate-spin h-3 w-3" fill="none" viewBox="0 0 24 24">
                                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                Updating...
                              </span>
                            ) : (
                              'Mark Ignore'
                            )}
                          </button>
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="relative">
                        <input
                          type="text"
                          placeholder="Filter results..."
                          value={filterText}
                          onChange={(e) => setFilterText(e.target.value)}
                          className="pl-8 pr-3 py-1.5 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                        <svg className="absolute left-2.5 top-2 w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                        </svg>
                      </div>
                      {filterText && (
                        <button
                          onClick={() => setFilterText("")}
                          className="text-slate-400 hover:text-slate-600 p-1"
                        >
                          <XMarkIcon className="w-4 h-4" />
                        </button>
                      )}
                      <div className="relative" style={{ position: 'relative', zIndex: showInstructionModal ? 0 : 40 }}>
                        <button
                          onClick={() => setStatusDropdownOpen(!statusDropdownOpen)}
                          className={`status-filter-button pl-2 pr-2 py-1.5 text-xs border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent cursor-pointer w-20 flex items-center justify-between ${statusFilter === 'found' ? 'bg-green-50 border-green-300 hover:bg-green-100' :
                            statusFilter === 'not found' ? 'bg-red-50 border-red-300 hover:bg-red-100' :
                              statusFilter === 'ignore' ? 'bg-gray-50 border-gray-300 hover:bg-gray-100' :
                                statusFilter === 'confirmed' ? 'bg-purple-50 border-purple-300 hover:bg-purple-100' :
                                  'bg-slate-50 border-slate-300 hover:bg-slate-100'
                            }`}
                          style={{ display: showSessionInstructionsModal ? 'none' : 'flex' }}
                        >
                          <span className={`truncate flex-1 mr-1 text-left ${statusFilter === 'found' ? 'text-green-800' :
                            statusFilter === 'not found' ? 'text-red-800' :
                              statusFilter === 'ignore' ? 'text-gray-800' :
                                statusFilter === 'confirmed' ? 'text-purple-800' :
                                  'text-slate-700'
                            }`}>
                            {statusFilter === 'found' ? 'Found' :
                              statusFilter === 'not found' ? 'Not Found' :
                                statusFilter === 'ignore' ? 'Ignore' :
                                  statusFilter === 'confirmed' ? 'Confirmed' :
                                    'All'}
                          </span>
                          <svg className="w-4 h-4 text-slate-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        </button>
                        {statusDropdownOpen && !showSessionInstructionsModal && !showInstructionModal && (
                          <div className="absolute top-full left-0 mt-1 bg-white border border-slate-200 rounded-lg shadow-lg z-40 min-w-32 max-h-48 overflow-y-auto status-dropdown" style={{ maxWidth: '200px', position: 'absolute', top: '100%', left: 0, marginTop: '4px' }}>
                            <div className="py-1">
                              <button
                                onClick={() => { setStatusFilter(""); setStatusDropdownOpen(false); }}
                                className={`w-full text-left px-3 py-2 text-sm hover:bg-slate-50 flex items-center gap-2 ${!statusFilter ? 'bg-blue-50' : ''
                                  }`}
                              >
                                All
                              </button>
                              <button
                                onClick={() => { setStatusFilter("found"); setStatusDropdownOpen(false); }}
                                className={`w-full text-left px-3 py-2 text-sm hover:bg-slate-50 flex items-center gap-2 ${statusFilter === 'found' ? 'bg-green-50' : ''
                                  }`}
                              >
                                <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                                Found
                              </button>
                              <button
                                onClick={() => { setStatusFilter("not found"); setStatusDropdownOpen(false); }}
                                className={`w-full text-left px-3 py-2 text-sm hover:bg-slate-50 flex items-center gap-2 ${statusFilter === 'not found' ? 'bg-red-50' : ''
                                  }`}
                              >
                                <span className="w-2 h-2 bg-red-500 rounded-full flex-shrink-0"></span>
                                <span className="whitespace-nowrap">Not Found</span>
                              </button>
                              <button
                                onClick={() => { setStatusFilter("ignore"); setStatusDropdownOpen(false); }}
                                className={`w-full text-left px-3 py-2 text-sm hover:bg-slate-50 flex items-center gap-2 ${statusFilter === 'ignore' ? 'bg-gray-50' : ''
                                  }`}
                              >
                                <span className="w-2 h-2 bg-gray-500 rounded-full"></span>
                                Ignore
                              </button>
                              <button
                                onClick={() => { setStatusFilter("confirmed"); setStatusDropdownOpen(false); }}
                                className={`w-full text-left px-3 py-2 text-sm hover:bg-slate-50 flex items-center gap-2 ${statusFilter === 'confirmed' ? 'bg-purple-50' : ''
                                  }`}
                              >
                                <span className="w-2 h-2 bg-purple-500 rounded-full"></span>
                                Confirmed
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Success Messages */}
                  {showSuccessMessage && (
                    <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg flex items-center gap-2">
                      <svg className="w-5 h-5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      <span className="text-sm font-medium text-green-700">Status updated successfully!</span>
                    </div>
                  )}

                  {showSessionInstructionsSuccess && (
                    <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg flex items-center gap-2">
                      <svg className="w-5 h-5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      <span className="text-sm font-medium text-green-700">{sessionInstructionsSuccess}</span>
                    </div>
                  )}

                  <div className="bg-white border border-slate-200 rounded-lg h-[900px] flex flex-col relative" style={{ height: '700px', maxHeight: '900px', overflow: 'hidden', position: 'relative' }}>
                    <div className="overflow-y-auto" style={{ height: 'calc(900px - 60px)', maxHeight: 'calc(900px - 60px)', overflowX: 'hidden', overflowY: 'auto', paddingRight: '8px' }}>
                      <style>
                        {`
                              .px-4 {
                                padding-right: 0.75rem !important;
                              }
                              
                              /* Ensure dropdowns appear on top of everything */
                              .status-dropdown {
                                z-index: 999999 !important;
                                position: absolute !important;
                                transform: translate3d(0, 0, 0) !important;
                                will-change: transform !important;
                                isolation: isolate !important;
                              }
                              
                              /* Ensure status buttons are not cut off */
                              .status-button {
                                white-space: nowrap !important;
                                overflow: visible !important;
                              }
                              
                              /* Ensure table rows maintain natural height */
                              .analysis-table tbody tr {
                                height: auto !important;
                                min-height: auto !important;
                                flex: none !important;
                              }
                              
                              /* Ensure table cells maintain natural height */
                              .analysis-table tbody td {
                                height: auto !important;
                                min-height: auto !important;
                                flex: none !important;
                              }
                            `}
                      </style>
                      <table className="w-full divide-y divide-slate-200 relative mr-4 analysis-table">
                        <thead className="bg-slate-100 sticky top-0 z-50" style={{ position: 'sticky', top: 0, zIndex: 50, backgroundColor: '#f1f5f9' }}>
                          <tr>
                            <th className="px-4 py-3 text-center text-xs font-medium text-slate-700 uppercase tracking-wider w-12 bg-slate-100">
                              <input
                                type="checkbox"
                                checked={selectAll}
                                onChange={handleSelectAll}
                                className="w-4 h-4 text-blue-600 bg-white border-gray-300 rounded focus:ring-blue-500 focus:ring-2 cursor-pointer"
                              />
                            </th>
                            <th className="px-4 py-3 text-center text-xs font-medium text-slate-700 uppercase tracking-wider bg-slate-100" style={{ width: '40px' }}>

                            </th>
                            <th
                              className="px-4 py-3 text-left text-xs font-medium text-slate-700 uppercase tracking-wider w-1/5 cursor-pointer hover:bg-slate-200 transition-colors bg-slate-100"
                              onClick={() => handleSort('key')}
                            >
                              <div className="flex items-center gap-2">
                                Key
                                {getSortIndicator('key')}
                              </div>
                            </th>
                            <th
                              className="px-4 py-3 text-left text-xs font-medium text-slate-700 uppercase tracking-wider w-2/5 cursor-pointer hover:bg-slate-200 transition-colors bg-slate-100"
                              onClick={() => handleSort('value')}
                            >
                              <div className="flex items-center gap-2">
                                Value
                                {getSortIndicator('value')}
                              </div>
                            </th>
                            <th
                              className="px-4 py-3 text-left text-xs font-medium text-slate-700 uppercase tracking-wider w-1/5 cursor-pointer hover:bg-slate-200 transition-colors bg-slate-100"
                              onClick={() => handleSort('label')}
                            >
                              <div className="flex items-center gap-2">
                                Label
                                {getSortIndicator('label')}
                              </div>
                            </th>
                            <th className="px-4 py-3 text-center text-xs font-medium text-slate-700 uppercase tracking-wider bg-slate-100" style={{ width: '40px' }}>
                              Instructions
                            </th>
                            <th
                              className="px-4 py-3 text-left text-xs font-medium text-slate-700 uppercase tracking-wider cursor-pointer hover:bg-slate-200 transition-colors bg-slate-100"
                              style={{ minWidth: '110px', width: '110px' }}
                              onClick={() => handleSort('status')}
                            >
                              <div className="flex items-center gap-2">
                                Status
                                {getSortIndicator('status')}
                              </div>
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-slate-200 relative">
                          {filteredAnalysisResults.map((item, index) => (
                            <tr key={index} className={`hover:bg-slate-50 relative ${editingStatusIndex === index ? 'z-30' : 'z-10'}`}>
                              <td className="px-4 py-3 text-center">
                                <input
                                  type="checkbox"
                                  checked={selectedRows.has(index)}
                                  onChange={() => handleRowSelect(index)}
                                  className="w-4 h-4 text-blue-600 bg-white border-gray-300 rounded focus:ring-blue-500 focus:ring-2 cursor-pointer"
                                />
                              </td>
                              <td className="px-4 py-3 text-center">
                                <button
                                  className="p-1 text-red-400 hover:text-red-600 transition-colors"
                                  onClick={() => handleDeleteClick(item)}
                                  title="Delete row"
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                  </svg>
                                </button>
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-slate-900 align-middle">
                                {typeof item.key === 'string' ? item.key.split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()).join(' ') :
                                  typeof item.key === 'object' ? JSON.stringify(item.key) :
                                    String(item.key || '')}
                              </td>
                              <td className="px-4 py-3 text-sm text-slate-700 align-middle">
                                <div className="break-words leading-tight">
                                  {typeof item.value === 'string' ? item.value :
                                    typeof item.value === 'object' ? JSON.stringify(item.value) :
                                      String(item.value || '')}
                                </div>
                              </td>
                              <td className="px-4 py-3 text-sm text-slate-700 align-middle">
                                <div className="text-slate-500 italic">
                                  {typeof item.label === 'string' ? item.label : ""}
                                </div>
                              </td>
                              <td className="px-4 py-3 text-sm text-slate-700 align-middle">
                                <div className="flex justify-center">
                                  <button
                                    className={`p-2 rounded-lg transition-colors ${item.instructions
                                      ? 'text-blue-600 hover:text-blue-700 bg-blue-50 hover:bg-blue-100'
                                      : 'text-slate-400 hover:text-slate-600 bg-slate-50 hover:bg-slate-100'
                                      }`}
                                    onClick={() => openInstructionModal(index, item.key, item.instructions, item.value)}
                                    title={item.instructions ? "Edit instructions" : "Add instructions"}
                                  >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                    </svg>
                                  </button>
                                </div>
                              </td>
                              <td className="px-4 py-3 text-sm relative" style={{ position: 'relative', minWidth: '110px', width: '110px' }}>
                                <div className="relative z-40" style={{ position: 'relative', zIndex: 40 }}>
                                  <button
                                    onClick={() => handleStatusClick(index)}
                                    className={`status-button inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium cursor-pointer hover:opacity-80 transition-opacity ${(item.status || '').toLowerCase() === 'found' ? 'bg-green-100 text-green-800 border border-green-300' :
                                      (item.status || '').toLowerCase() === 'not found' ? 'bg-red-100 text-red-800 border border-red-300' :
                                        (item.status || '').toLowerCase() === 'ignore' ? 'bg-gray-100 text-gray-800 border border-gray-300' :
                                          (item.status || '').toLowerCase() === 'confirmed' ? 'bg-purple-100 text-purple-800 border border-purple-300' :
                                            'bg-gray-100 text-gray-800 border border-gray-300'
                                      }`}
                                    title={(item.status || (item.value && item.value !== 'Empty' ? 'found' : 'not found')).toLowerCase() === 'not found' ? 'Not Found' :
                                      (item.status || (item.value && item.value !== 'Empty' ? 'found' : 'not found')).toLowerCase() === 'confirmed' ? 'Confirmed' :
                                        (item.status || (item.value && item.value !== 'Empty' ? 'found' : 'not found')).charAt(0).toUpperCase() + (item.status || (item.value && item.value !== 'Empty' ? 'found' : 'not found')).slice(1).toLowerCase()}
                                  >
                                    <span className="whitespace-nowrap min-w-0 flex-1">
                                      {(item.status || (item.value && item.value !== 'Empty' ? 'found' : 'not found')).toLowerCase() === 'not found' ? 'Not Found' :
                                        (item.status || (item.value && item.value !== 'Empty' ? 'found' : 'not found')).toLowerCase() === 'confirmed' ? 'Confirmed' :
                                          (item.status || (item.value && item.value !== 'Empty' ? 'found' : 'not found')).charAt(0).toUpperCase() + (item.status || (item.value && item.value !== 'Empty' ? 'found' : 'not found')).slice(1).toLowerCase()}
                                    </span>
                                    <svg className="ml-1 w-3 h-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                    </svg>
                                  </button>

                                  {/* Dropdown Menu */}
                                  {editingStatusIndex === index && (
                                    <div className="absolute top-full left-0 mt-1 bg-white border border-slate-200 rounded-lg shadow-lg z-40 min-w-32 max-h-48 overflow-y-auto status-dropdown" style={{ position: 'absolute', top: '100%', left: 0, marginTop: '4px', backgroundColor: 'white', border: '1px solid #e2e8f0', borderRadius: '8px', boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)', maxHeight: '192px', overflowY: 'auto', maxWidth: '200px' }}>
                                      <div className="py-1">
                                        <button
                                          onClick={() => selectStatus(index, 'found')}
                                          className={`w-full text-left px-3 py-2 text-sm hover:bg-slate-50 flex items-center gap-2 ${(item.status || (item.value && item.value !== 'Empty' ? 'found' : 'not found')).toLowerCase() === 'found' ? 'bg-green-50' : ''
                                            }`}
                                        >
                                          <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                                          Found
                                        </button>
                                        <button
                                          onClick={() => selectStatus(index, 'not found')}
                                          className={`w-full text-left px-3 py-2 text-sm hover:bg-slate-50 flex items-center gap-2 ${(item.status || (item.value && item.value !== 'Empty' ? 'found' : 'not found')).toLowerCase() === 'not found' ? 'bg-red-50' : ''
                                            }`}
                                        >
                                          <span className="w-2 h-2 bg-red-500 rounded-full"></span>
                                          Not Found
                                        </button>
                                        <button
                                          onClick={() => selectStatus(index, 'ignore')}
                                          className={`w-full text-left px-3 py-2 text-sm hover:bg-slate-50 flex items-center gap-2 ${(item.status || (item.value && item.value !== 'Empty' ? 'found' : 'not found')).toLowerCase() === 'ignore' ? 'bg-gray-50' : ''
                                            }`}
                                        >
                                          <span className="w-2 h-2 bg-gray-500 rounded-full"></span>
                                          Ignore
                                        </button>
                                        <button
                                          onClick={() => selectStatus(index, 'confirmed')}
                                          className={`w-full text-left px-3 py-2 text-sm hover:bg-slate-50 flex items-center gap-2 ${(item.status || '').toLowerCase() === 'confirmed' ? 'bg-purple-50' : ''
                                            }`}
                                        >
                                          <span className="w-2 h-2 bg-purple-500 rounded-full"></span>
                                          Confirmed
                                        </button>
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}

              {/* Chunk Response Grid Section - Main Content Area */}
              {chunkResponse && chunkResponse.all_chunks && (
                <div className="mb-6">
                  <div className="mb-0">
                    <label className="block text-lg font-semibold text-slate-700 mb-0 -mb-1">
                      Select Response ({chunkResponse.chunks_available} available):
                    </label>
                  </div>

                  <div className="mb-0">
                    <div className="flex items-center gap-3 -mb-1">
                      <div className="flex-1">
                        <div className="text-sm text-slate-600 mb-0 -mt-2">
                          {(selectedChunks.size > 0 || selectedFields.size > 0) ? (
                            <div className="space-y-1">
                              {selectedChunks.size > 0 && (
                                <div>
                                  {selectedChunks.size} chunk{selectedChunks.size > 1 ? 's' : ''} selected: {Array.from(selectedChunks).map(chunkIndex => {
                                    const chunk = chunkResponse.all_chunks.find(c => c.chunk_index === chunkIndex);
                                    return `Chunk ${chunk ? chunk.chunk_index + 1 : chunkIndex + 1}`;
                                  }).join(', ')}
                                </div>
                              )}
                              {selectedFields.size > 0 && selectedChunks.size === 0 && (
                                <div>
                                  {selectedFields.size} field{selectedFields.size > 1 ? 's' : ''} selected individually
                                </div>
                              )}
                            </div>
                          ) : "Select chunks or individual fields from the grid below..."}
                        </div>
                      </div>

                      <div className="flex flex-col items-end gap-1">
                        <button
                          className="px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-base font-semibold disabled:opacity-60"
                          onClick={handleConfirmChunk}
                          disabled={(selectedChunks.size === 0 && selectedFields.size === 0) || confirmLoading}
                        >
                          {confirmLoading ? "Confirming..." : "Confirm Selection"}
                        </button>

                        {/* Grid Expander Button - positioned under Confirm button */}
                        <button
                          onClick={toggleGridExpansion}
                          className="p-2 text-slate-600 hover:text-slate-800 hover:bg-slate-100 rounded-lg transition-colors"
                          title={isGridExpanded ? "Collapse grid" : "Expand grid to full screen"}
                        >
                          {isGridExpanded ? (
                            <span className="material-symbols-outlined text-slate-600">hide</span>
                          ) : (
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
                            </svg>
                          )}
                        </button>
                      </div>
                    </div>
                  </div>

                  {confirmError && (
                    <div className="mb-4">
                      <div className="text-red-600 text-sm mb-2 font-medium">{typeof confirmError === 'string' ? confirmError : JSON.stringify(confirmError)}</div>
                    </div>
                  )}

                  {/* Grid Display */}
                  {isGridExpanded && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 z-40" onClick={toggleGridExpansion}></div>
                  )}
                  <div className={`bg-white border border-slate-200 rounded-lg overflow-hidden transition-all duration-300 ${isGridExpanded
                    ? 'fixed inset-0 z-50 m-4 bg-white shadow-2xl rounded-lg flex flex-col'
                    : 'h-[820px] flex flex-col'
                    }`} style={!isGridExpanded ? { height: '820px', maxHeight: '820px', overflow: 'hidden' } : {}}>
                    {isGridExpanded && (
                      <div className="flex items-center justify-between p-4 border-b border-slate-200 bg-slate-50 flex-shrink-0">
                        <h3 className="text-lg font-semibold text-slate-700">Chunk Response Grid (Full Screen)</h3>
                        <button
                          onClick={toggleGridExpansion}
                          className="p-2 text-slate-600 hover:text-slate-800 hover:bg-slate-200 rounded-lg transition-colors"
                          title="Exit full screen"
                        >
                          <span className="material-symbols-outlined text-slate-600">hide</span>
                        </button>
                      </div>
                    )}
                    <div className={`${isGridExpanded ? 'flex-1 overflow-hidden' : 'flex-1 overflow-hidden'}`} style={!isGridExpanded ? { overflow: 'hidden', maxHeight: '820px' } : {}}>
                      <div className={`${isGridExpanded ? 'h-full overflow-auto' : 'h-full overflow-auto'}`} style={!isGridExpanded ? { height: '820px', overflow: 'auto', maxHeight: '820px' } : { overflow: 'auto' }}>
                        <table className={`${isGridExpanded ? 'h-full w-full' : 'w-full'}`} style={{ minWidth: isGridExpanded ? '1200px' : 'auto' }}>
                          <thead className={`bg-gray-700 border-b border-slate-200 sticky top-0 z-10`}>
                            <tr>
                              <th className="px-4 py-3 text-left text-sm font-medium text-white min-w-32 sticky left-0 z-20 bg-gray-700">
                                Labels
                              </th>
                              {chunkResponse.all_chunks.map((chunk) => {
                                const isSelected = selectedChunks.has(chunk.chunk_index);
                                return (
                                  <th key={chunk.chunk_index} className={`px-4 py-3 text-left text-sm font-medium text-white min-w-32 ${isSelected ? 'bg-blue-600' : ''}`}>
                                    <div className="flex items-center gap-2">
                                      <input
                                        type="checkbox"
                                        checked={isSelected}
                                        onChange={() => {
                                          let newSelectedChunks = new Set();
                                          let newSelectedFields = new Set();

                                          if (isSelected) {
                                            // Unselecting chunk - clear everything
                                            // Keep other individual field selections that are not from this chunk
                                            selectedFields.forEach(fieldKey => {
                                              const [, chunkIndex] = fieldKey.split('|');
                                              if (parseInt(chunkIndex) !== chunk.chunk_index) {
                                                newSelectedFields.add(fieldKey);
                                              }
                                            });
                                          } else {
                                            // Selecting chunk - only allow one chunk at a time
                                            // Clear all previous selections and select this chunk
                                            newSelectedChunks.add(chunk.chunk_index);

                                            // Automatically select all fields in this chunk
                                            // Get all unique field names from the current chunk response (same logic as grid)
                                            const allFields = new Set();
                                            chunkResponse.all_chunks.forEach((chunkItem) => {
                                              if (typeof chunkItem.response === 'string') {
                                                chunkItem.response.split('\n').forEach(line => {
                                                  const [key] = line.split(':');
                                                  if (key && key.trim()) {
                                                    const normalizedKey = key.trim().toLowerCase();
                                                    allFields.add(normalizedKey);
                                                  }
                                                });
                                              }
                                            });

                                            // Add all fields from this chunk to selection
                                            Array.from(allFields).forEach(fieldName => {
                                              const fieldKey = `${fieldName}|${chunk.chunk_index}`;
                                              newSelectedFields.add(fieldKey);
                                            });
                                          }

                                          setSelectedChunks(newSelectedChunks);
                                          setSelectedFields(newSelectedFields);
                                        }}
                                        className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
                                      />
                                      <span className="text-white whitespace-nowrap">Chunk {chunk.chunk_index + 1}</span>
                                    </div>
                                  </th>
                                );
                              })}
                            </tr>
                          </thead>
                          <tbody className={`${isGridExpanded ? '' : ''}`}>
                            {(() => {
                              // ALWAYS START FRESH - Get field names ONLY from current chunkResponse
                              const allFields = new Set();

                              // Only process if we have fresh chunkResponse data
                              if (chunkResponse && chunkResponse.all_chunks && chunkResponse.all_chunks.length > 0) {
                                console.log('Processing FRESH chunks:', chunkResponse.all_chunks.length);

                                // Clear any previous data and only use current chunks
                                chunkResponse.all_chunks.forEach((chunk, chunkIndex) => {
                                  if (typeof chunk.response === 'string') {
                                    chunk.response.split('\n').forEach(line => {
                                      const [key] = line.split(':');
                                      if (key && key.trim()) {
                                        const normalizedKey = key.trim().toLowerCase();
                                        allFields.add(normalizedKey);
                                      }
                                    });
                                  }
                                });
                              }

                              const sortedFields = Array.from(allFields).sort();
                              console.log('FRESH unique fields found:', sortedFields);
                              return sortedFields;
                            })().map((field, index) => (
                              <tr key={field} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-100'}>
                                <td className={`px-4 py-3 text-sm font-medium text-slate-700 sticky left-0 z-20 ${index % 2 === 0 ? 'bg-white' : 'bg-gray-100'}`}>
                                  {field.split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}
                                </td>
                                {chunkResponse.all_chunks.map((chunk) => {
                                  // Parse chunk response into key-value pairs with normalized keys
                                  const chunkData = {};
                                  if (typeof chunk.response === 'string') {
                                    chunk.response.split('\n').forEach(line => {
                                      const [key, ...valueParts] = line.split(':');
                                      if (key && valueParts.length > 0) {
                                        const value = valueParts.join(':').trim();
                                        const normalizedKey = key.trim().toLowerCase();
                                        chunkData[normalizedKey] = value;
                                      }
                                    });
                                  }

                                  const isChunkSelected = selectedChunks.has(chunk.chunk_index);
                                  const fieldKey = `${field}|${chunk.chunk_index}`;
                                  const isFieldSelected = selectedFields.has(fieldKey);

                                  // Check if any other field in this row is selected
                                  const isRowFieldSelected = Array.from(selectedFields).some(selectedKey => {
                                    const [selectedField] = selectedKey.split('|');
                                    return selectedField === field && selectedKey !== fieldKey;
                                  });

                                  // Check if any chunk is selected (disables all individual fields)
                                  // const anyChunkSelected = selectedChunks.size > 0; // No longer needed with new logic

                                  const baseBgColor = index % 2 === 0 ? 'bg-white' : 'bg-gray-100';
                                  const selectedBgColor = isChunkSelected ? 'bg-blue-100' : (isFieldSelected ? 'bg-blue-100' : '');
                                  // If field exists in chunkData (even if empty), show its value or "Empty"
                                  // If field doesn't exist in chunkData at all, show "not found"
                                  const fieldValue = field in chunkData 
                                    ? (chunkData[field] || 'Empty')
                                    : 'not found';

                                  return (
                                    <td key={`${chunk.chunk_index}-${field}`} className={`px-4 py-3 text-sm text-slate-700 ${baseBgColor} ${selectedBgColor} relative`}>
                                      <div className="relative">
                                        {fieldValue}
                                        {/* Individual field checkbox - positioned absolutely to not affect layout */}
                                        <input
                                          type="checkbox"
                                          checked={isFieldSelected}
                                          disabled={isRowFieldSelected && !isFieldSelected}
                                          onChange={() => {
                                            if (isRowFieldSelected && !isFieldSelected) {
                                              // Cannot select if another field in same row is already selected
                                              return;
                                            }

                                            let newSelectedFields = new Set(selectedFields);
                                            let newSelectedChunks = new Set(selectedChunks);

                                            if (isFieldSelected) {
                                              // Unselecting field - simple removal
                                              newSelectedFields.delete(fieldKey);

                                              // If this chunk was selected as a whole, unselect it when any field is unselected
                                              if (newSelectedChunks.has(chunk.chunk_index)) {
                                                newSelectedChunks.delete(chunk.chunk_index);
                                              }
                                            } else {
                                              // Selecting field - clear chunk selection and any fields from the same row
                                              newSelectedChunks.clear();

                                              // Remove any fields from the same row (but keep fields from other rows)
                                              const fieldsToKeep = Array.from(newSelectedFields).filter(selectedKey => {
                                                const [selectedField] = selectedKey.split('|');
                                                return selectedField !== field; // Keep fields from different rows
                                              });

                                              newSelectedFields = new Set(fieldsToKeep);

                                              // Add the new field selection
                                              newSelectedFields.add(fieldKey);
                                            }

                                            // Auto-check chunk header if all fields in this chunk are now selected
                                            const allFieldsInThisChunk = new Set();
                                            chunkResponse.all_chunks.forEach((chunkItem) => {
                                              if (typeof chunkItem.response === 'string') {
                                                chunkItem.response.split('\n').forEach(line => {
                                                  const [key] = line.split(':');
                                                  if (key && key.trim()) {
                                                    const normalizedKey = key.trim().toLowerCase();
                                                    allFieldsInThisChunk.add(normalizedKey);
                                                  }
                                                });
                                              }
                                            });

                                            const selectedFieldsInThisChunk = Array.from(newSelectedFields).filter(selectedKey => {
                                              const [, chunkIndex] = selectedKey.split('|');
                                              return parseInt(chunkIndex) === chunk.chunk_index;
                                            });

                                            // If all fields in this chunk are selected, auto-check the chunk
                                            if (selectedFieldsInThisChunk.length === allFieldsInThisChunk.size) {
                                              newSelectedChunks.add(chunk.chunk_index);
                                            }

                                            setSelectedFields(newSelectedFields);
                                            setSelectedChunks(newSelectedChunks);
                                          }}
                                          className={`absolute top-0 right-0 w-3 h-3 text-blue-600 bg-white border-gray-300 rounded focus:ring-blue-500 ${(isRowFieldSelected && !isFieldSelected) ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer'
                                            }`}
                                          title={
                                            (isRowFieldSelected && !isFieldSelected) ? 'Only one field per row can be selected' :
                                              `Select field: ${field.split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}`
                                          }
                                        />
                                      </div>
                                    </td>
                                  );
                                })}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}

          {/* Manual Fields Tab */}
          {activeTab === "manual" && (
            <div className="space-y-6">
              {/* Header with Create Button */}
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-slate-700">Manual Fields</h3>
                <button
                  onClick={() => setShowCreateManualFieldModal(true)}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
                >
                  <PlusIcon className="w-4 h-4" />
                  Add Field
                </button>
              </div>

              {/* Error Message */}
              {manualFieldError && (
                <div className="text-red-600 text-sm p-3 bg-red-50 border border-red-200 rounded-lg mb-4">
                  {manualFieldError}
                </div>
              )}

              {/* Loading State */}
              {loadingManualFields && (
                <div className="text-slate-500 text-center py-8">
                  <div className="flex items-center justify-center gap-2">
                    <svg className="animate-spin h-5 w-5 text-slate-400" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Loading manual fields...
                  </div>
                </div>
              )}

              {/* Manual Fields Table */}
              {!loadingManualFields && (
                <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
                  {manualFields.length === 0 ? (
                    <div className="text-center py-12 text-slate-500">
                      <div className="mb-4">
                        <svg className="mx-auto h-12 w-12 text-slate-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                      </div>
                      <p className="text-lg font-medium text-slate-900 mb-2">No manual fields yet</p>
                      <p className="text-slate-600 mb-4">Create your first manual field to get started.</p>
                      <button
                        onClick={() => setShowCreateManualFieldModal(true)}
                        className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
                      >
                        <PlusIcon className="w-4 h-4" />
                        Add Field
                      </button>
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full divide-y divide-slate-200">
                        <thead className="bg-slate-100">
                          <tr>
                            <th className="px-4 py-3 text-left text-xs font-medium text-slate-700 uppercase tracking-wider">
                              Field Name
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-slate-700 uppercase tracking-wider">
                              Created
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-slate-700 uppercase tracking-wider">
                              Updated
                            </th>
                            <th className="px-4 py-3 text-center text-xs font-medium text-slate-700 uppercase tracking-wider" style={{ width: '100px' }}>
                              Actions
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-slate-200">
                          {manualFields.map((field) => (
                            <tr key={field.id} className="hover:bg-slate-50 transition-colors">
                              <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-slate-900">
                                {field.name}
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap text-sm text-slate-700">
                                {field.created ? new Date(field.created).toLocaleString() : 'Unknown'}
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap text-sm text-slate-700">
                                {field.updated ? new Date(field.updated).toLocaleString() : 'Unknown'}
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap text-center">
                                <div className="flex items-center justify-center gap-2">
                                  <button
                                    onClick={() => openEditModal(field)}
                                    className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
                                    title="Edit field"
                                  >
                                    <PencilIcon className="w-4 h-4" />
                                  </button>
                                  <button
                                    onClick={() => showDeleteConfirmation(field)}
                                    disabled={deletingManualField}
                                    className="p-2 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
                                    title="Delete field"
                                  >
                                    <TrashIcon className="w-4 h-4" />
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Error Modal Popup */}
      {showErrorModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-red-50 border-2 border-red-200 rounded-xl p-6 w-full max-w-md mx-4">
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="flex-shrink-0">
                  <svg className="w-6 h-6 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold text-red-800">Unable to Generate Response</h3>
              </div>
              <button
                onClick={() => setShowErrorModal(false)}
                className="text-red-400 hover:text-red-600 p-1"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <p className="text-red-700 font-medium mb-6">{typeof reportError === 'string' ? reportError : JSON.stringify(reportError)}</p>

            <div className="flex justify-end">
              <button
                onClick={() => setShowErrorModal(false)}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Finalize Error Modal Popup */}
      {showFinalizeErrorModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-red-50 border-2 border-red-200 rounded-xl p-6 w-full max-w-md mx-4">
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="flex-shrink-0">
                  <svg className="w-6 h-6 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold text-red-800">Unable to Finalize EMR Type</h3>
              </div>
              <button
                onClick={() => setShowFinalizeErrorModal(false)}
                className="text-red-400 hover:text-red-600 p-1"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <p className="text-red-700 font-medium mb-6">{typeof finalizeError === 'string' ? finalizeError : JSON.stringify(finalizeError)}</p>

            <div className="flex justify-end">
              <button
                onClick={() => setShowFinalizeErrorModal(false)}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Analyze Error Modal Popup */}
      {showAnalyzeErrorModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-6 w-full max-w-lg mx-4">
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="flex-shrink-0">
                  <svg className="w-6 h-6 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold text-blue-800">Analysis Complete</h3>
              </div>
              <button
                onClick={() => setShowAnalyzeErrorModal(false)}
                className="text-blue-400 hover:text-blue-600 p-1"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="mb-6">
              <p className="text-blue-700 font-medium mb-3">Great news! All fields have been analyzed.</p>
              <p className="text-blue-600 text-sm leading-relaxed">
                {typeof analyzeError === 'string' ? analyzeError : JSON.stringify(analyzeError)}
              </p>
            </div>

            <div className="flex justify-end">
              <button
                onClick={() => setShowAnalyzeErrorModal(false)}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Got it!
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Instruction Modal */}
      {showInstructionModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center" style={{ zIndex: 10000 }}>
          <div className="bg-white rounded-xl p-6 w-full max-w-2xl mx-4 max-h-[80vh] overflow-y-auto relative" style={{ zIndex: 10001 }}>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-slate-800">Edit Instructions</h2>
              <button
                onClick={() => {
                  setShowInstructionModal(false);
                  setEditingInstruction("");
                  setEditingIndex(null);
                  setEditingKey("");
                  setEditingValue("");
                }}
                className="text-slate-400 hover:text-slate-600"
              >
                <XMarkIcon className="w-6 h-6" />
              </button>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Instructions for: <span className="font-semibold">{editingKey}</span>
              </label>
              <textarea
                className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm resize-none"
                placeholder="Enter instructions for this field..."
                rows="12"
                value={editingInstruction}
                onChange={(e) => setEditingInstruction(e.target.value)}
                style={{ '--tw-placeholder-color': '#9CA3AF' }}
              />
            </div>

            <div className="flex items-center justify-end gap-3">
              <button
                onClick={() => {
                  setShowInstructionModal(false);
                  setEditingInstruction("");
                  setEditingIndex(null);
                  setEditingKey("");
                  setEditingValue("");
                }}
                className="px-4 py-2 bg-slate-200 text-slate-700 rounded-lg hover:bg-slate-300 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={saveModalInstruction}
                disabled={savingModalInstruction}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 flex items-center gap-2"
              >
                {savingModalInstruction ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Saving...
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    Save
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-md mx-4">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-slate-800">Confirm Delete</h2>
              <button
                onClick={handleDeleteCancel}
                className="text-slate-400 hover:text-slate-600"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="mb-6">
              <p className="text-slate-700 mb-2">Are you sure you want to delete this item?</p>
              <div className="bg-slate-50 p-3 rounded-lg">
                <p className="text-sm text-slate-600">
                  <span className="font-medium">Key:</span> {deleteItem?.key}
                </p>
                <p className="text-sm text-slate-600">
                  <span className="font-medium">Value:</span> {deleteItem?.value}
                </p>
              </div>
            </div>

            <div className="flex items-center justify-end gap-3">
              <button
                onClick={handleDeleteCancel}
                className="px-4 py-2 bg-slate-200 text-slate-700 rounded-lg hover:bg-slate-300 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteConfirm}
                disabled={deleteLoading}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 flex items-center gap-2"
              >
                {deleteLoading ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Deleting...
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                    Delete
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Success Notification */}
      {showDeleteSuccess && (
        <div className="fixed top-4 right-4 bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg z-50 flex items-center gap-2 animate-in slide-in-from-top-2">
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
          <span className="font-medium">Item deleted successfully!</span>
        </div>
      )}

      {/* Create Manual Field Modal */}
      {showCreateManualFieldModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-md mx-4">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-slate-800">Create Manual Field</h2>
              <button
                onClick={() => {
                  setShowCreateManualFieldModal(false);
                  setManualFieldError("");
                  setSelectedLookupField(null);
                }}
                className="text-slate-400 hover:text-slate-600"
              >
                <XMarkIcon className="w-6 h-6" />
              </button>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Lookup Not-For-Analyzing EMR Type Field
              </label>
              <select
                value={selectedLookupField ? selectedLookupField.id : ""}
                onChange={(e) => {
                  if (e.target.value) {
                    const selectedField = createManualFieldOptions.find(field => field.id === e.target.value);
                    if (selectedField) {
                      setSelectedLookupField(selectedField);
                    }
                  } else {
                    setSelectedLookupField(null);
                  }
                }}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent mb-3"
                disabled={loadingEmrTypeFields}
                required
              >
                <option value="">
                  {loadingEmrTypeFields ? 'Loading not-for-analyzing EMR Type Fields...' : 'Select a Field...'}
                </option>
                {createManualFieldOptions.map((field) => (
                  <option key={field.id} value={field.id}>
                    {field.name} ({field.type})
                  </option>
                ))}
              </select>

              {emrTypeFieldsError && (
                <p className="text-red-600 text-xs mb-2">{emrTypeFieldsError}</p>
              )}

              {/* Show selected field details */}
              {selectedLookupField && (
                <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <h4 className="text-sm font-medium text-blue-800 mb-2">Selected Field Details:</h4>
                  <div className="space-y-1 text-xs text-blue-700">
                    <div><span className="font-medium">Name:</span> {selectedLookupField.name}</div>
                    <div><span className="font-medium">Type:</span> {selectedLookupField.type}</div>
                    <div><span className="font-medium">Analyzable:</span> {selectedLookupField.analyzable}</div>
                    {selectedLookupField.type === 'dropdown' && selectedLookupField.dropdown_values && (
                      <div>
                        <span className="font-medium">Dropdown Options:</span>
                        <div className="mt-1 flex flex-wrap gap-1">
                          {selectedLookupField.dropdown_values.split('\n').filter(val => val.trim()).map((option, idx) => (
                            <span key={idx} className="inline-block bg-blue-100 text-blue-700 text-xs px-2 py-1 rounded border border-blue-200">
                              {option.trim()}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {!selectedLookupField && (
                <p className="text-xs text-slate-500 mt-3">
                  You must select a field from the dropdown above
                </p>
              )}
            </div>

            {manualFieldError && (
              <div className="mb-4 text-red-600 text-sm p-3 bg-red-50 border border-red-200 rounded-lg">
                {manualFieldError}
              </div>
            )}

            <div className="flex items-center justify-end gap-3">
              <button
                onClick={() => {
                  setShowCreateManualFieldModal(false);
                  setManualFieldError("");
                  setSelectedLookupField(null);
                }}
                className="px-4 py-2 bg-slate-200 text-slate-700 rounded-lg hover:bg-slate-300 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={createManualField}
                disabled={creatingManualField || !selectedLookupField}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center gap-2"
              >
                {creatingManualField ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Creating...
                  </>
                ) : (
                  <>
                    <PlusIcon className="w-4 h-4" />
                    Create Field
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Manual Field Modal */}
      {showEditManualFieldModal && editingManualField && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-md mx-4">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-slate-800">Edit Manual Field</h2>
              <button
                onClick={() => {
                  setShowEditManualFieldModal(false);
                  setEditingManualField(null);
                  setEditingManualFieldName("");
                  setManualFieldError("");
                  setOriginalManualFieldId(null);
                }}
                className="text-slate-400 hover:text-slate-600"
              >
                <XMarkIcon className="w-6 h-6" />
              </button>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Select EMR Type Field
              </label>
              <select
                value={editingManualField?.id || ""}
                onChange={(e) => {
                  if (e.target.value) {
                    const selectedField = emrTypeFields.find(field => field.id === e.target.value);
                    if (selectedField) {
                      setEditingManualField(selectedField);
                      setEditingManualFieldName(selectedField.name);
                    }
                  } else {
                    setEditingManualField(null);
                    setEditingManualFieldName("");
                  }
                }}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                disabled={loadingEmrTypeFields}
                required
              >
                <option value="">
                  {loadingEmrTypeFields ? 'Loading EMR Type Fields...' : 'Select a Field...'}
                </option>
                {emrTypeFields.map((field) => (
                  <option key={field.id} value={field.id}>
                    {field.name} ({field.type})
                  </option>
                ))}
              </select>

              {emrTypeFieldsError && (
                <p className="text-red-600 text-xs mb-2">{emrTypeFieldsError}</p>
              )}

              {/* Show selected field details */}
              {editingManualField && (
                <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <h4 className="text-sm font-medium text-blue-800 mb-2">Selected Field Details:</h4>
                  <div className="space-y-1 text-xs text-blue-700">
                    <div><span className="font-medium">Name:</span> {editingManualField.name}</div>
                    <div><span className="font-medium">Type:</span> {editingManualField.type}</div>
                    <div><span className="font-medium">Analyzable:</span> {editingManualField.analyzable}</div>
                    {editingManualField.type === 'dropdown' && editingManualField.dropdown_values && (
                      <div>
                        <span className="font-medium">Dropdown Options:</span>
                        <div className="mt-1 flex flex-wrap gap-1">
                          {editingManualField.dropdown_values.split('\n').filter(val => val.trim()).map((option, idx) => (
                            <span key={idx} className="inline-block bg-blue-100 text-blue-700 text-xs px-2 py-1 rounded border border-blue-200">
                              {option.trim()}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {!editingManualField && (
                <p className="text-xs text-slate-500 mt-3">
                  You must select a field from the dropdown above
                </p>
              )}
            </div>

            {manualFieldError && (
              <div className="mb-4 text-red-600 text-sm p-3 bg-red-50 border border-red-200 rounded-lg">
                {manualFieldError}
              </div>
            )}

            <div className="flex items-center justify-end gap-3">
              <button
                onClick={() => {
                  setShowEditManualFieldModal(false);
                  setEditingManualField(null);
                  setEditingManualFieldName("");
                  setManualFieldError("");
                  setOriginalManualFieldId(null);
                }}
                className="px-4 py-2 bg-slate-200 text-slate-700 rounded-lg hover:bg-slate-300 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={updateManualField}
                disabled={updatingManualField || !editingManualField}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center gap-2"
              >
                {updatingManualField ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Updating...
                  </>
                ) : (
                  <>
                    <PencilIcon className="w-4 h-4" />
                    Update Field
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Manual Field Confirmation Modal */}
      {showDeleteManualFieldModal && fieldToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-md mx-4">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-slate-800">Delete Manual Field</h2>
              <button
                onClick={() => {
                  setShowDeleteManualFieldModal(false);
                  setFieldToDelete(null);
                }}
                className="text-slate-400 hover:text-slate-600"
              >
                <XMarkIcon className="w-6 h-6" />
              </button>
            </div>

            <div className="mb-6">
              <p className="text-slate-700 mb-2">Are you sure you want to delete this manual field?</p>
              <div className="bg-slate-50 p-3 rounded-lg">
                <p className="text-sm text-slate-600">
                  <span className="font-medium">Field Name:</span> {fieldToDelete.name}
                </p>
              </div>
            </div>

            {manualFieldError && (
              <div className="mb-4 text-red-600 text-sm p-3 bg-red-50 border border-red-200 rounded-lg">
                {manualFieldError}
              </div>
            )}

            <div className="flex items-center justify-end gap-3">
              <button
                onClick={() => {
                  setShowDeleteManualFieldModal(false);
                  setFieldToDelete(null);
                }}
                className="px-4 py-2 bg-slate-200 text-slate-700 rounded-lg hover:bg-slate-300 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={deleteManualField}
                disabled={deletingManualField}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 flex items-center gap-2"
              >
                {deletingManualField ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Deleting...
                  </>
                ) : (
                  <>
                    <TrashIcon className="w-4 h-4" />
                    Delete Field
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Session Instructions Modal */}
      {showSessionInstructionsModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-6xl mx-4 overflow-hidden flex flex-col" style={{ height: '90vh', minHeight: '90vh' }}>
            <div className="flex items-center justify-between mb-4 flex-shrink-0">
              <h2 className="text-xl font-bold text-slate-800">Session Instructions</h2>
              <button
                onClick={() => {
                  setShowSessionInstructionsModal(false);
                  setSessionInstructions("");
                  setMethodsInstructions("");
                  setProgressTowardsGoalInstructions("");
                  setRecommendedChangesInstructions("");
                  setTopHeaderText("");
                  setSection1Header("Section 1: Field \"Methods\"");
                  setSection2Header("Section 2: Field \"Progress_towards_goal\"");
                  setSection3Header("Section 3: Field \"Recommended_changes\"");
                  setSessionInstructionsError("");
                }}
                className="text-slate-400 hover:text-slate-600"
              >
                <XMarkIcon className="w-6 h-6" />
              </button>
            </div>

            <div className="mb-4 flex-1 overflow-y-auto">
              {/* Main Instructions Header - Show top content from methods_instructions */}
              {topHeaderText && (
                <div className="mb-6 p-4 bg-slate-50 border border-slate-200 rounded-lg">
                  <div className="text-slate-800 mb-3 whitespace-pre-wrap">{topHeaderText}</div>
                  <p className="text-slate-700 mb-2">For each session, I need 3 notes as follows:</p>
                  <ul className="text-slate-700 space-y-1 mb-3">
                    <li><strong>Section 1:</strong> Field "Methods"</li>
                    <li><strong>Section 2:</strong> Field "Progress_towards_goal"</li>
                    <li><strong>Section 3:</strong> Field "Recommended_changes"</li>
                  </ul>
                  <p className="text-slate-700 font-medium">Session Notes Examples and Templates</p>
                </div>
              )}

              {/* Section 1: Methods - Always visible */}
              <div className="mb-6">
                <h4 className="text-lg font-bold text-slate-800 mb-3 bg-blue-50 p-3 rounded-lg border border-blue-200">
                  {section1Header}
                </h4>
                <textarea
                  value={methodsInstructions}
                  onChange={(e) => setMethodsInstructions(e.target.value)}
                  placeholder="Enter methods used in this session..."
                  className="w-full h-48 px-3 py-2 border-2 border-slate-300 rounded-lg focus:outline-none focus:ring-4 focus:ring-blue-300 focus:border-blue-500 resize-none transition-all duration-200"
                  disabled={savingSessionInstructions}
                />
              </div>

              {/* Section 2: Progress towards goal - Always visible */}
              <div className="mb-6">
                <h4 className="text-lg font-bold text-slate-800 mb-3 bg-green-50 p-3 rounded-lg border border-green-200">
                  {section2Header}
                </h4>
                <textarea
                  value={progressTowardsGoalInstructions}
                  onChange={(e) => setProgressTowardsGoalInstructions(e.target.value)}
                  placeholder="Enter progress towards goals in this session..."
                  className="w-full h-48 px-3 py-2 border-2 border-slate-300 rounded-lg focus:outline-none focus:ring-4 focus:ring-blue-300 focus:border-blue-500 resize-none transition-all duration-200"
                  disabled={savingSessionInstructions}
                />
              </div>

              {/* Section 3: Recommended changes - Always visible */}
              <div className="mb-6">
                <h4 className="text-lg font-bold text-slate-800 mb-3 bg-purple-50 p-3 rounded-lg border border-purple-200">
                  {section3Header}
                </h4>
                <textarea
                  value={recommendedChangesInstructions}
                  onChange={(e) => setRecommendedChangesInstructions(e.target.value)}
                  placeholder="Enter recommended changes for next session..."
                  className="w-full h-48 px-3 py-2 border-2 border-slate-300 rounded-lg focus:outline-none focus:ring-4 focus:ring-blue-300 focus:border-blue-500 resize-none transition-all duration-200"
                  disabled={savingSessionInstructions}
                />
              </div>


            </div>

            {sessionInstructionsError && (
              <div className="mb-4 text-red-600 text-sm p-3 bg-red-50 border border-red-200 rounded-lg">
                {sessionInstructionsError}
              </div>
            )}

            {/* Clean separator line */}
            <div className="border-t border-slate-200 mb-4 flex-shrink-0"></div>

            <div className="flex items-center justify-end gap-3 flex-shrink-0">
              <button
                onClick={() => {
                  setShowSessionInstructionsModal(false);
                  setSessionInstructions("");
                  setMethodsInstructions("");
                  setProgressTowardsGoalInstructions("");
                  setRecommendedChangesInstructions("");
                  setTopHeaderText("");
                  setSection1Header("Section 1: Field \"Methods\"");
                  setSection2Header("Section 2: Field \"Progress_towards_goal\"");
                  setSection3Header("Section 3: Field \"Recommended_changes\"");
                  setSessionInstructionsError("");
                }}
                className="px-4 py-2 bg-slate-200 text-slate-700 rounded-lg hover:bg-slate-300 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={saveSessionInstructions}
                disabled={savingSessionInstructions || (!methodsInstructions.trim() && !progressTowardsGoalInstructions.trim() && !recommendedChangesInstructions.trim())}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center gap-2"
              >
                {savingSessionInstructions ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Saving...
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    Save Instructions
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EMRTypeDetails; 
